import React, { useState, useEffect } from 'react';
// Indian-style numeric grouping only; labels in this screen say “tokens”.
import { formatCredits } from '../../utils/numberFormat';
import MerchantVerificationDialog, { VerificationChip, formatDateTimeIST } from './MerchantVerificationDialog';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  LinearProgress,
  Tooltip,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  CardHeader,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search,
  Download,
  Refresh,
  Delete,
  CheckCircle,
  Error,
  Cancel,
  AccountBalanceWallet,
  Add,
  Visibility,
  Edit,
  Block,
  Business,
  CalendarToday,
  TrendingUp,
  Warning,
  VerifiedUser,
  HowToReg,
  InfoOutlined,
  AutoAwesome,
  History,
  Remove,
  AccountBalance,
  Email,
  Description,
  ContentCopy,
  AutoFixHigh,
  ZoomIn,
  Notifications,
  Campaign,
  QrCode,
  Inventory,
  PersonAdd,
  PersonRemove,
  Image,
  Info,
  Phone,
  WhatsApp,
  CompareArrows,
  Movie,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { merchantManagementAPI, masterDataConfigAPI, adminNotificationAPI, topicNotificationHistoryAPI, dashboardAPI, referralCodeManagementAPI } from '../../services/api';
import { merchantAuthAPI } from '../../services/merchantAPI';
import { usePermissions } from '../../contexts/PermissionContext';
import { API_URLS } from '../../config/apiConfig';
import { isBustModel, isHumanModel, isIndiaCountry, isActiveStatus } from '../../config/businessConstants';
import { getSecurePreviewUrl } from '../../utils/imageUrlUtils';
import { getAdminSecurePreviewUrl, getAdminGeneratedMediaUrl, getAdminSecureVideoStreamUrl } from '../../utils/adminImageUtils';
import SecureImage from '../Common/SecureImage';
import SecureVideo from '../Common/SecureVideo';
import GenerationCompareDialog from './GenerationCompareDialog';

// Helper function to convert watermarked_image_url to secure admin URL
const getSecureAdminUrl = (watermarkedImageUrl) => {
  if (!watermarkedImageUrl) return null;

  // Extract generation ID from URL like "/api/merchant/ai-generation/preview/123"
  const match = watermarkedImageUrl.match(/\/preview\/(\d+)/);
  if (match) {
    const generationId = parseInt(match[1]);
    return getAdminSecurePreviewUrl(generationId);
  }

  return watermarkedImageUrl; // Fallback to original URL
};

// Admin catalog stream URL for the AI model face image linked to a generation (model_face_image_id)
const getAdminModelFaceCatalogUrl = (modelFaceImageId) => {
  if (!modelFaceImageId) return null;
  return `${API_URLS.BACKEND_BASE}/api/admin/model-face/images/${modelFaceImageId}/file`;
};

// Keep admin create/edit consent payload aligned with public merchant registration.
const TERMS_POLICY_VERSION = '2025-08-15';
const PRIVACY_POLICY_VERSION = '2025-08-15';

// Media Preview CardHeaders: one title line only (model catalog name goes in CardContent) so every tile header matches height
const adminMediaPreviewHeaderTitleSx = { color: '#fff', fontSize: '1rem', fontWeight: 700, lineHeight: 1.3 };
// Shared bar layout — same minHeight as Upload / Generated so the purple tile does not look taller
const adminMediaPreviewCardHeaderBarSx = {
  py: 0,
  px: 2,
  minHeight: 56,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  '& .MuiCardHeader-avatar': { mr: 1.5 },
  '& .MuiCardHeader-content': { alignSelf: 'center' }
};

// Shared card shell so Media Preview tiles align (rounded clip + border like product / output cards)
const adminMediaPreviewCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 2,
  overflow: 'hidden',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider'
};

const adminTableDenseCellSx = {
  py: 0.75,
  px: 1,
  fontSize: '0.9rem',
  lineHeight: 1.2
};

const adminTableActionIconButtonSx = {
  p: 0.75,
  '& .MuiSvgIcon-root': {
    fontSize: '1.3rem'
  }
};

// MerchantManagement.js
// This component manages merchants and their token balance (API fields may still use credit_* keys).
// Tokens can be added (positive value) or deducted (negative value)
// Both individual and bulk token adjustments are supported
// Error messages are displayed in popup dialogs for better UX
// 
// REFERRAL CODE DISPLAY:
// - Shows referral code used during merchant registration
// - Displays merchant's own assigned referral code (if any)
// - Provides clear indication of how merchant joined the platform
// - Shows referral code details with registration date
// 
// AUTO-ASSIGNMENT:
// - Backend automatically assigns referral codes to new merchants
// - Referral codes are assigned during merchant creation and updates
// - Ensures all merchants have referral codes for sharing
// - Uses available codes from the referral code pool
// 
// GLOBAL DATE FORMAT: DD/MM/YYYY
// All dates throughout the application are now formatted as DD/MM/YYYY (e.g., 18/08/2025)
// This provides better readability and follows international date standards
// 
// EMAIL VERIFICATION:
// - Admins can verify merchant emails using OTP verification
// - Email verification status is displayed with icons (VerifiedUser for verified, Email for unverified)
// - Verification icons appear before email addresses in tables and dialogs
// - OTP verification dialog allows admins to send and verify OTP codes
// - Email verification status can be managed in create/edit forms
// 
// TOKEN BALANCE VALIDATION:
// - Super admin is restricted from adding negative token values when creating/editing merchants
// - Token balance field has validation to prevent negative values
// - Form validation includes token balance validation
// - Input fields have min=0 attribute and helper text to guide users
//
// WHATSAPP PHONE (admin create/edit):
// - Matches merchant profile: one WhatsApp row (dial code + number); mobile/mobile_code are synced for the API
// - Address "Country" change updates dial codes to that country’s code
//
// GST VALIDATION:
// - Comprehensive GST number validation with detailed error messages
// - Errors are displayed below input boxes for better UX
// - GST is now a required field for merchant registration
// - Fixed duplicate error message display - now shows only once in helperText
//
// LOCATION DROPDOWN FIXES:
// - Fixed states not loading in dropdown by improving state management
// - Used functional state updates to prevent stale closure issues
// - Added proper error handling for API calls
// - Improved timing for loading states and cities when editing merchants
// - Added validation to ensure countryId and stateId are valid before making API calls
//
// GST ERROR HANDLING:
// - GST number already registered errors now show below the input box instead of popup
// - Errors are displayed in the same format as other validation errors
// - Errors are cleared when user starts typing in the GST field
//
// WHATSAPP VALIDATION (admin form):
// - Indian numbers (country_id === 2): 10 digits starting with 6-9; other countries: 5–15 digits
//
// REFERRAL CODE PAGINATION FIX:
// - Fixed pagination not working by consolidating loading logic into separate functions
// - Removed duplicate useEffect hooks that were causing conflicting API calls
// - Separated concerns: one useEffect for initial load, one for pagination/filter changes, one for filter reset
// - Proper state management ensures pagination works correctly without infinite loops

// Helper: format processing time in seconds to a human-readable string
const formatProcessingTime = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (remainMins > 0) return `${hrs}h ${remainMins}m`;
  return `${hrs}h`;
};

const MerchantManagement = () => {
  const { hasPermission } = usePermissions();

  // All hooks must be called at the top level
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Track actually applied search
  const [appliedStatusFilter, setAppliedStatusFilter] = useState(''); // Track actually applied status
  // Account verification filter; preset from ?verification= (link in the admin new-signup email)
  const initialVerificationFilter = (() => {
    const v = new URLSearchParams(window.location.search).get('verification');
    return ['pending', 'approved', 'rejected'].includes(v) ? v : '';
  })();
  const [verificationFilter, setVerificationFilter] = useState(initialVerificationFilter);
  const [appliedVerificationFilter, setAppliedVerificationFilter] = useState(initialVerificationFilter);
  const [verifyMerchant, setVerifyMerchant] = useState(null);
  const [grantFreeTokens, setGrantFreeTokens] = useState(false); // Add/Edit: verify for free tokens on save
  const [pendingVerificationCount, setPendingVerificationCount] = useState(0);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [stats, setStats] = useState({});
  const [maxCreditLimit, setMaxCreditLimit] = useState(10000000); // Default fallback for MAX_CREDIT_ALLOCATION_LIMIT

  // Dialog states
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [creditsDialog, setCreditsDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [otpDialog, setOtpDialog] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  // Password link states
  const [passwordLinkSending, setPasswordLinkSending] = useState(false);
  const [verificationConfirmDialog, setVerificationConfirmDialog] = useState(false);
  const [pendingFormSubmit, setPendingFormSubmit] = useState(false);

  // AI Generation View Dialog states
  const [aiGenerationViewDialog, setAiGenerationViewDialog] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  // Index of the currently shown clip in the reel "Source Clips" carousel
  const [reelClipIndex, setReelClipIndex] = useState(0);
  const [apiLogsSummary, setApiLogsSummary] = useState(null);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  // Side-by-side AI model reference vs generated output (admin generation details)
  const [aiModelGeneratedCompareOpen, setAiModelGeneratedCompareOpen] = useState(false);
  // Side-by-side customer reference photo vs generated output (same UX as AI model compare)
  const [customerPhotoGeneratedCompareOpen, setCustomerPhotoGeneratedCompareOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Referral Code Management states
  const [referralCodes, setReferralCodes] = useState([]);
  const [referralCodesLoading, setReferralCodesLoading] = useState(false);
  const [referralCodesPage, setReferralCodesPage] = useState(0);
  const [referralCodesRowsPerPage, setReferralCodesRowsPerPage] = useState(20);
  const [referralCodesTotalCount, setReferralCodesTotalCount] = useState(0);
  const [referralCodesSearchTerm, setReferralCodesSearchTerm] = useState('');
  const [referralCodesStatusFilter, setReferralCodesStatusFilter] = useState('');
  const [referralCodesMerchantFilter, setReferralCodesMerchantFilter] = useState('');
  const [referralCodeStats, setReferralCodeStats] = useState({});

  // Referral Code Dialog states
  const [referralCodeAssignDialog, setReferralCodeAssignDialog] = useState(false);
  const [referralCodeReleaseDialog, setReferralCodeReleaseDialog] = useState(false);
  const [selectedReferralCode, setSelectedReferralCode] = useState(null);
  const [assignMerchantId, setAssignMerchantId] = useState('');
  const [referralCodeDialogError, setReferralCodeDialogError] = useState('');
  const [referralCodeDialogSuccess, setReferralCodeDialogSuccess] = useState('');
  const [referralCodeDialogLoading, setReferralCodeDialogLoading] = useState(false);

  // AI Generation History states
  const [aiGenerations, setAiGenerations] = useState([]);
  const [aiGenerationsLoading, setAiGenerationsLoading] = useState(false);
  const [aiGenerationsPage, setAiGenerationsPage] = useState(0);
  const [aiGenerationsRowsPerPage, setAiGenerationsRowsPerPage] = useState(20);
  const [aiGenerationsTotalCount, setAiGenerationsTotalCount] = useState(0);
  const [aiGenerationsSearchTerm, setAiGenerationsSearchTerm] = useState('');
  const [aiGenerationsStatusFilter, setAiGenerationsStatusFilter] = useState('');
  const [aiGenerationsModelTypeFilter, setAiGenerationsModelTypeFilter] = useState('');
  const [aiGenerationsOutputFormatFilter, setAiGenerationsOutputFormatFilter] = useState('');
  const [aiGenerationsCategoryFilter, setAiGenerationsCategoryFilter] = useState('');
  const [aiGenerationsClothingTypeFilter, setAiGenerationsClothingTypeFilter] = useState('');
  const [aiGenerationsGenderFilter, setAiGenerationsGenderFilter] = useState('');
  const [aiGenerationsEthnicityFilter, setAiGenerationsEthnicityFilter] = useState('');
  const [aiGenerationsBustColorFilter, setAiGenerationsBustColorFilter] = useState('');
  const [aiGenerationsBustFinishFilter, setAiGenerationsBustFinishFilter] = useState('');
  const [aiGenerationsSourceFilter, setAiGenerationsSourceFilter] = useState('');
  const [aiGenerationsMerchantFilter, setAiGenerationsMerchantFilter] = useState('');
  const [aiGenerationsBatchIdFilter, setAiGenerationsBatchIdFilter] = useState('');
  const [aiGenerationsFromDate, setAiGenerationsFromDate] = useState('');
  const [aiGenerationsToDate, setAiGenerationsToDate] = useState('');
  const [aiGenerationsDateError, setAiGenerationsDateError] = useState('');
  const [copyNotification, setCopyNotification] = useState(false);
  const [modelTypes, setModelTypes] = useState([]);
  const [outputFormats, setOutputFormats] = useState([]);
  const [generationStatuses, setGenerationStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clothingTypes, setClothingTypes] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [ethnicities, setEthnicities] = useState([]);
  const [bustColors, setBustColors] = useState([]);
  const [bustFinishes, setBustFinishes] = useState([]);
  const [restrictedDomains, setRestrictedDomains] = useState([]);

  // Location dropdown states
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Form states
  const [suspendReason, setSuspendReason] = useState('');
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [bulkAction, setBulkAction] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkCredits, setBulkCredits] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [bulkDialogError, setBulkDialogError] = useState('');
  const [suspendDialogError, setSuspendDialogError] = useState('');

  // Merchant form state (mobile/mobile_code mirror WhatsApp on submit for API; no separate mobile row in UI)
  const [merchantForm, setMerchantForm] = useState({
    name: '',
    email: '',
    mobile: '',
    mobile_code: '+91',  // Hard default +91
    company_name: '',
    gst_number: '',
    address: '',
    country_id: 2,       // Hard default India ID
    state_id: '',
    city_id: '',
    pincode: '',
    // Password field removed - merchants set password via email link
    credits_balance: 100,
    is_active: true,
    email_verified: false,
    mobile_verified: false,
    whatsapp_number: '',
    whatsapp_code: '+91', // Hard default +91
    terms_accepted: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState(''); // General form error for dialog

  // Broadcast Notification states
  const [topicNotificationDialog, setTopicNotificationDialog] = useState(false);
  const [topicNotificationForm, setTopicNotificationForm] = useState({
    title: '',
    body: '',
    image_url: '',
    topic_message: '',
    data: {}
  });
  const [topicNotificationLoading, setTopicNotificationLoading] = useState(false);
  const [topicNotificationError, setTopicNotificationError] = useState('');
  const [topicNotificationSuccess, setTopicNotificationSuccess] = useState('');

  // Topic Notification History states
  const [topicNotificationHistory, setTopicNotificationHistory] = useState([]);
  const [topicNotificationHistoryLoading, setTopicNotificationHistoryLoading] = useState(false);
  const [topicNotificationHistoryError, setTopicNotificationHistoryError] = useState('');
  const [topicNotificationHistoryStats, setTopicNotificationHistoryStats] = useState({});
  const [topicNotificationHistoryPage, setTopicNotificationHistoryPage] = useState(0);
  const [topicNotificationHistoryRowsPerPage, setTopicNotificationHistoryRowsPerPage] = useState(10);
  const [topicNotificationHistoryTotal, setTopicNotificationHistoryTotal] = useState(0);
  const [topicNotificationHistorySearchTerm, setTopicNotificationHistorySearchTerm] = useState('');
  const [topicNotificationHistoryStatusFilter, setTopicNotificationHistoryStatusFilter] = useState('');
  const [selectedTopicNotification, setSelectedTopicNotification] = useState(null);
  const [topicNotificationViewDialog, setTopicNotificationViewDialog] = useState(false);
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  // Refund Dialog
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  // Zoom Dialog states
  const [zoomDialog, setZoomDialog] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  // Helper function to get merchant status
  // Priority: suspended > deleted > inactive > pending (email not verified) > active
  const getMerchantStatus = (merchant) => {
    if (merchant.is_suspended) {
      return 'suspended';
    } else if (merchant.status === 0) {
      return 'deleted';
    } else if (!merchant.is_active) {
      return 'inactive';
    } else if (!merchant.email_verified) {
      return 'pending';
    } else {
      return 'active';
    }
  };

  // Check permissions
  const canView = hasPermission('MerchantManagement', 'view');
  const canCreate = hasPermission('MerchantManagement', 'create');
  const canUpdate = hasPermission('MerchantManagement', 'update');
  const canDelete = hasPermission('MerchantManagement', 'delete');
  const canExport = hasPermission('MerchantManagement', 'export');
  const canVerify = hasPermission('MerchantManagement', 'verify');

  // AI Generation specific permissions
  const canViewAIGenerationPrompts = hasPermission('MerchantManagement', 'view_ai_generation_prompts');
  const canProcessAIGenerationRefund = hasPermission('MerchantManagement', 'process_ai_generation_refund');

  // Main useEffect for loading merchants - triggers on pagination changes only
  // Note: Search and filters are now only applied when search button is clicked
  // Main useEffect for loading merchants - triggers on pagination changes only
  // Note: Search and filters are now only applied when search button is clicked
  useEffect(() => {
    if (canView) {
      loadMerchants();
      loadStats();
      loadIndustryTypes();
      loadCountries();
      loadMasterData();
      loadSystemSettings(); // Load system settings including MAX_CREDIT_ALLOCATION_LIMIT
    }
  }, [page, rowsPerPage, canView, sortBy, sortOrder]);

  // Reload when the verification filter is applied from the stat card (initial load is above)
  const verificationFilterMounted = React.useRef(false);
  useEffect(() => {
    if (!verificationFilterMounted.current) {
      verificationFilterMounted.current = true;
      return;
    }
    if (canView) loadMerchants();
  }, [appliedVerificationFilter]);

  // Auto-close success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load AI generation history when tab is active
  useEffect(() => {
    if (activeTab === 1 && canView) {
      // Load initial data when tab becomes active
      loadAIGenerationHistory();
    }
  }, [activeTab, canView]);

  // Handle AI generation history pagination changes
  useEffect(() => {
    if (activeTab === 1 && canView) {
      loadAIGenerationHistory();
    }
  }, [aiGenerationsPage, aiGenerationsRowsPerPage, canView]);

  // Load topic notification history when tab is active
  useEffect(() => {
    if (activeTab === 2 && canView) {
      loadTopicNotificationHistory();
      loadTopicNotificationStats();
    }
  }, [activeTab, topicNotificationHistoryPage, topicNotificationHistoryRowsPerPage, topicNotificationHistorySearchTerm, topicNotificationHistoryStatusFilter, canView]);

  // Load referral codes when tab is active
  useEffect(() => {
    if (activeTab === 3 && canView) {
      loadReferralCodes();
      loadReferralCodeStats();
    }
  }, [activeTab, canView]);

  // Handle referral code pagination and filter changes
  useEffect(() => {
    if (activeTab === 3 && canView) {
      loadReferralCodes();
    }
  }, [referralCodesPage, referralCodesRowsPerPage, referralCodesSearchTerm, referralCodesStatusFilter, activeTab, canView]);

  // Handle referral code search and filter changes - reset page to 0
  useEffect(() => {
    if (activeTab === 3) {
      setReferralCodesPage(0); // Reset to first page when filters change
    }
  }, [referralCodesSearchTerm, referralCodesStatusFilter, activeTab]);

  // ============================================================================
  // PAGINATION RESET LOGIC: Reset page to 0 when search terms or filters change
  // This prevents the issue where searching while on page > 0 shows no results
  // because the filtered results might not have enough pages to reach the current page
  // ============================================================================


  // Handle AI generation history tab changes - reset page to 0
  useEffect(() => {
    if (activeTab === 1) {
      setAiGenerationsPage(0); // Reset to first page when tab becomes active
    }
  }, [activeTab]);

  // Handle topic notification history search and filter changes - reset page to 0
  useEffect(() => {
    if (activeTab === 2) {
      setTopicNotificationHistoryPage(0); // Reset to first page when search terms or filters change
    }
  }, [topicNotificationHistorySearchTerm, topicNotificationHistoryStatusFilter, activeTab]);

  // Close compare overlays when the generation details dialog is dismissed
  useEffect(() => {
    if (!aiGenerationViewDialog) {
      setAiModelGeneratedCompareOpen(false);
      setCustomerPhotoGeneratedCompareOpen(false);
    }
    setReelClipIndex(0); // reset reel carousel when the dialog opens/closes
  }, [aiGenerationViewDialog]);

  // If user doesn't have view permission, show access denied
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to view Merchant Management.
        </Alert>
      </Container>
    );
  }

  const loadMerchants = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        search: appliedSearchTerm,
        status: appliedStatusFilter,
        verification_status: appliedVerificationFilter,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      console.log('Loading merchants with params:', params);
      const response = await merchantManagementAPI.getAll(params);
      console.log('Merchants loaded:', response.data.merchants?.length || 0, 'total:', response.data.total || 0);
      setMerchants(response.data.merchants || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      setError('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchMerchants = async () => {
    console.log('Search button clicked - searchTerm:', searchTerm, 'statusFilter:', statusFilter);
    // Reset to first page when searching
    setPage(0);
    // Update applied filters to match current input values
    setAppliedSearchTerm(searchTerm);
    setAppliedStatusFilter(statusFilter);
    setAppliedVerificationFilter(verificationFilter);
    console.log('Applied filters set - appliedSearchTerm:', searchTerm, 'appliedStatusFilter:', statusFilter);

    // Load merchants with the current filter values directly instead of relying on state updates
    setLoading(true);
    try {
      const params = {
        page: 1, // Reset to first page
        per_page: rowsPerPage,
        search: searchTerm,
        status: statusFilter,
        verification_status: verificationFilter,
        sort_by: sortBy, // Added
        sort_order: sortOrder // Added
      };

      console.log('Loading merchants with params:', params);
      const response = await merchantManagementAPI.getAll(params);
      console.log('Merchants loaded:', response.data.merchants?.length || 0, 'total:', response.data.total || 0);
      setMerchants(response.data.merchants || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      setError('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  // Clear merchant filters
  const handleClearMerchantFilters = async () => {
    // Reset all filter states
    setSearchTerm('');
    setStatusFilter('');
    setAppliedSearchTerm(''); // Clear applied search
    setAppliedStatusFilter(''); // Clear applied status
    setVerificationFilter('');
    setAppliedVerificationFilter('');
    setPage(0);

    // Load merchants with cleared filters directly instead of relying on state updates
    setLoading(true);
    try {
      const params = {
        page: 1, // Reset to first page
        per_page: rowsPerPage,
        search: '', // Clear search
        status: '', // Clear status filter
        sort_by: sortBy, // Added
        sort_order: sortOrder // Added
      };

      console.log('Loading merchants with cleared params:', params);
      const response = await merchantManagementAPI.getAll(params);
      console.log('Merchants loaded after clear:', response.data.merchants?.length || 0, 'total:', response.data.total || 0);
      setMerchants(response.data.merchants || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load merchants after clear:', error);
      setError('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingVerificationCount = async () => {
    try {
      const response = await merchantManagementAPI.getStats();
      setPendingVerificationCount(response.data.stats?.pending_verification || 0);
    } catch (error) {
      console.error('Failed to load pending verification count:', error);
    }
  };

  // Stat card click: show merchants awaiting verification
  const showPendingVerification = () => {
    setVerificationFilter('pending');
    setAppliedVerificationFilter('pending');
    setPage(0);
  };

  const handleVerificationDone = async (message) => {
    const merchantId = verifyMerchant?.id;
    setVerifyMerchant(null);
    setSuccess(message);
    loadMerchants();
    loadPendingVerificationCount();

    // Refresh the open View dialog so it shows the new status and token balance
    if (viewDialog && merchantId && selectedMerchant?.id === merchantId) {
      try {
        const response = await merchantManagementAPI.getById(merchantId);
        const fresh = response.data?.merchant || response.data;
        if (fresh?.id === merchantId) {
          setSelectedMerchant(prev => (prev?.id === merchantId ? { ...prev, ...fresh } : prev));
        }
      } catch (error) {
        console.error('Failed to refresh merchant details:', error);
      }
    }
  };

  const loadStats = async () => {
    loadPendingVerificationCount();
    try {
      // Use the new dashboard merchant stats API for more accurate data
      // This API provides role-based filtering and accurate counts
      // It ensures only active records are counted and applies proper filtering
      const response = await dashboardAPI.getMerchantStats();
      if (response.data.success) {
        setStats(response.data.stats || {});
      } else {
        // Fallback to old API if new one fails
        const fallbackResponse = await merchantManagementAPI.getStats();
        setStats(fallbackResponse.data.stats || {});
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Fallback to old API if new one fails
      try {
        const fallbackResponse = await merchantManagementAPI.getStats();
        setStats(fallbackResponse.data.stats || {});
      } catch (fallbackError) {
        console.error('Failed to load stats from fallback API:', fallbackError);
      }
    }
  };

  const loadIndustryTypes = async () => {
    try {
      const response = await merchantManagementAPI.getIndustryTypes();
      const industryTypesList = response.data.industry_types || [];
      setIndustryTypes(industryTypesList);

      // Find jewelry industry type and set as default
      const jewelryIndustry = industryTypesList.find(
        type => type.name.toLowerCase() === 'jewelry' ||
          type.display_name.toLowerCase() === 'jewelry'
      );

      // Industry type is not shown in admin add/edit; default jewelry is applied on create submit only.
    } catch (error) {
      console.error('Failed to load industry types:', error);
    }
  };

  // Load system settings including MAX_CREDIT_ALLOCATION_LIMIT
  // This fetches the MAX_CREDIT_ALLOCATION_LIMIT from the backend for validation
  const loadSystemSettings = async () => {
    try {
      const response = await masterDataConfigAPI.getSystemSettings();
      // Extract and set the MAX_CREDIT_ALLOCATION_LIMIT from system settings if available
      if (
        response.data.settings &&
        response.data.settings.CREDITS &&
        Array.isArray(response.data.settings.CREDITS)
      ) {
        // Find the MAX_CREDIT_ALLOCATION_LIMIT setting in the CREDITS category
        const maxCreditSetting = response.data.settings.CREDITS.find(
          (setting) => setting.setting_key === "MAX_CREDIT_ALLOCATION_LIMIT"
        );
        if (maxCreditSetting && maxCreditSetting.typed_value) {
          setMaxCreditLimit(maxCreditSetting.typed_value);
        }
      }
    } catch (error) {
      console.error('Failed to load system settings:', error);
      // Keep using default fallback value (10,000,000)
    }
  };

  // Load countries for dropdown
  const loadCountries = async () => {
    try {
      const response = await masterDataConfigAPI.getCountries();
      setCountries(response.data.data || []);

      // Set default country to India (ID: 2) and mobile code to +91
      if (response.data.data && response.data.data.length > 0) {
        const indiaCountry = response.data.data.find(country => Number(country.id) === 2);
        if (indiaCountry) {
          setMerchantForm(prev => ({
            ...prev,
            country_id: indiaCountry.id,
            mobile_code: normalizeCode(indiaCountry.country_code),
            whatsapp_code: normalizeCode(indiaCountry.country_code)
          }));

          // Load states for India after setting the country
          setTimeout(() => {
            loadStates(indiaCountry.id);
          }, 100);
        } else {
          // If India not found, ensure +91 is still set as code
          setMerchantForm(prev => ({
            ...prev,
            mobile_code: '+91',
            whatsapp_code: '+91'
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  // Load master data for AI generation history filters
  const loadMasterData = async () => {
    try {
      const [
        modelTypesResponse,
        outputFormatsResponse,
        generationStatusesResponse,
        categoriesResponse,
        clothingTypesResponse,
        genderOptionsResponse,
        ethnicitiesResponse,
        bustColorsResponse,
        bustFinishesResponse,
        restrictedDomainsResponse
      ] = await Promise.all([
        masterDataConfigAPI.getModelTypes(),
        masterDataConfigAPI.getOutputFormats(),
        masterDataConfigAPI.getGenerationStatuses(),
        masterDataConfigAPI.getCategories(),
        masterDataConfigAPI.getClothingTypes(),
        masterDataConfigAPI.getGenderOptions(),
        masterDataConfigAPI.getEthnicities(),
        masterDataConfigAPI.getBustColors(),
        masterDataConfigAPI.getBustFinishes(),
        masterDataConfigAPI.getRestrictedDomains()
      ]);

      setModelTypes(modelTypesResponse.data.data || []);
      setOutputFormats(outputFormatsResponse.data.data || []);
      setGenerationStatuses(generationStatusesResponse.data.data || []);
      setCategories(categoriesResponse.data.data || []);
      setClothingTypes(clothingTypesResponse.data.data || []);
      setGenderOptions(genderOptionsResponse.data.data || []);
      setEthnicities(ethnicitiesResponse.data.data || []);
      setBustColors(bustColorsResponse.data.data || []);
      setBustFinishes(bustFinishesResponse.data.data || []);
      setRestrictedDomains(restrictedDomainsResponse.data.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  // Load AI generation history
  const loadAIGenerationHistory = async () => {
    setAiGenerationsLoading(true);
    try {
      const params = {
        page: aiGenerationsPage + 1,
        per_page: aiGenerationsRowsPerPage,
        search: aiGenerationsSearchTerm,
        status_id: aiGenerationsStatusFilter,
        model_type_id: aiGenerationsModelTypeFilter,
        output_format_id: aiGenerationsOutputFormatFilter,
        category_id: aiGenerationsCategoryFilter,
        clothing_type_id: aiGenerationsClothingTypeFilter,
        ethnicity_id: aiGenerationsEthnicityFilter,
        bust_color_id: aiGenerationsBustColorFilter,
        bust_finish_id: aiGenerationsBustFinishFilter,
        generation_source: aiGenerationsSourceFilter,
        gender_option_id: aiGenerationsGenderFilter,
        merchant_id: aiGenerationsMerchantFilter,
        batch_id: aiGenerationsBatchIdFilter,
        start_date: aiGenerationsFromDate,
        end_date: aiGenerationsToDate
      };

      console.log('AI Generation History API Parameters:', params);
      const response = await merchantManagementAPI.getAIGenerationHistory(params);
      console.log('AI Generation History Response:', response.data.generations);
      setAiGenerations(response.data.generations || []);
      setAiGenerationsTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load AI generation history:', error);
      setError('Failed to load AI generation history');
    } finally {
      setAiGenerationsLoading(false);
    }
  };

  // Load states for selected country
  const loadStates = async (countryId) => {
    if (!countryId) return;

    setLoadingStates(true);
    try {
      const response = await masterDataConfigAPI.getStatesByCountry(countryId);
      if (response.data && response.data.data) {
        setStates(response.data.data);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Failed to load states:', error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Load cities for selected state
  const loadCities = async (stateId) => {
    if (!stateId) return;

    setLoadingCities(true);
    try {
      const response = await masterDataConfigAPI.getCitiesByState(stateId);
      if (response.data && response.data.data) {
        setCities(response.data.data);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error('Failed to load cities:', error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Handle country change
  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    const selectedCountry = countries.find(country => Number(country.id) === Number(countryId));

    // Update form with new country_id, dial codes, and reset state_id and city_id
    setMerchantForm(prev => {
      const newMobileCode = selectedCountry ? normalizeCode(selectedCountry.country_code) : '+91';
      return {
        ...prev,
        country_id: countryId,
        mobile_code: newMobileCode,
        // Single phone field is WhatsApp in UI; keep dial code aligned with address country
        whatsapp_code: newMobileCode,
        state_id: '',
        city_id: ''
      };
    });

    // Clear cities when country changes
    setCities([]);

    // Clear any field errors
    setFormErrors(prev => ({
      ...prev,
      country_id: '',
      state_id: '',
      city_id: ''
    }));

    // Clear states first, then load new ones if countryId is valid
    setStates([]);
    if (countryId) {
      loadStates(countryId);
    }
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
    setPage(0); // Reset to first page when sorting
  };

  // Handle state change
  const handleStateChange = (e) => {
    const stateId = e.target.value;

    // Update form with new state_id and reset city_id
    setMerchantForm(prev => ({
      ...prev,
      state_id: stateId,
      city_id: ''
    }));

    // Clear cities when state changes
    setCities([]);

    // Clear any field errors
    setFormErrors(prev => ({
      ...prev,
      state_id: '',
      city_id: ''
    }));

    // Load cities for the selected state if stateId is valid
    if (stateId) {
      loadCities(stateId);
    }
  };

  // Handle city change
  const handleCityChange = (e) => {
    const cityId = e.target.value;

    // Update form with new city_id
    setMerchantForm(prev => ({
      ...prev,
      city_id: cityId
    }));

    // Clear any field errors
    setFormErrors(prev => ({
      ...prev,
      city_id: ''
    }));
  };

  const handleViewMerchant = (merchant) => {
    if (!canView) {
      setError('You don\'t have permission to view merchant details');
      return;
    }
    setSelectedMerchant(merchant);
    setViewDialog(true);
  };

  const handleSuspendMerchant = async () => {
    if (!canDelete) {
      setSuspendDialogError('You don\'t have permission to suspend merchants');
      return;
    }
    try {
      await merchantManagementAPI.suspend(selectedMerchant.id, suspendReason);
      setSuspendDialog(false);
      setSuspendReason('');
      setSuspendDialogError('');
      loadMerchants();
      loadStats();

      // Show success message
      setError(''); // Clear any previous errors
      setSuccess(`Merchant "${selectedMerchant.name}" suspended successfully`);
    } catch (error) {
      console.error('Failed to suspend merchant:', error);
      setSuspendDialogError(error.response?.data?.error || 'Failed to suspend merchant');
    }
  };

  const handleActivateMerchant = async (merchantId) => {
    if (!canUpdate) {
      setError('You don\'t have permission to activate merchants');
      return;
    }
    try {
      await merchantManagementAPI.activate(merchantId);
      loadMerchants();
      loadStats();

      // Find merchant name for success message
      const merchant = merchants.find(m => m.id === merchantId);
      const merchantName = merchant ? merchant.name : 'Merchant';

      // Show success message
      setError(''); // Clear any previous errors
      setSuccess(`${merchantName} activated successfully`);
    } catch (error) {
      console.error('Failed to activate merchant:', error);
      setError('Failed to activate merchant');
    }
  };

  const handleReactivateMerchant = async (merchantId) => {
    if (!canUpdate) {
      setError('You don\'t have permission to reactivate merchants');
      return;
    }
    try {
      await merchantManagementAPI.reactivate(merchantId);
      loadMerchants();
      loadStats();

      // Find merchant name for success message
      const merchant = merchants.find(m => m.id === merchantId);
      const merchantName = merchant ? merchant.name : 'Merchant';

      // Show success message
      setError(''); // Clear any previous errors
      setSuccess(`${merchantName} reactivated successfully`);
    } catch (error) {
      console.error('Failed to reactivate merchant:', error);
      setError('Failed to reactivate merchant');
    }
  };

  const handleAddCredits = async () => {
    if (!canCreate) {
      setDialogError('You don\'t have permission to adjust tokens');
      return;
    }

    // Validate token amount is not zero
    if (parseInt(creditsAmount) === 0) {
      setDialogError('Token amount cannot be zero');
      return;
    }

    try {
      const response = await merchantManagementAPI.addCredits(selectedMerchant.id, {
        credits: parseInt(creditsAmount),
        reason: creditsReason
      });
      setCreditsDialog(false);
      setCreditsAmount('');
      setCreditsReason('');
      setDialogError('');
      loadMerchants();
      loadStats();

      // Show success message based on operation
      setError(''); // Clear any previous errors
      if (parseInt(creditsAmount) > 0) {
        setSuccess(`${formatCredits(creditsAmount)} tokens added to ${selectedMerchant.name} successfully`);
      } else {
        setSuccess(`${formatCredits(Math.abs(parseInt(creditsAmount)))} tokens deducted from ${selectedMerchant.name} successfully`);
      }
    } catch (error) {
      console.error('Failed to adjust tokens:', error);
      setDialogError(error.response?.data?.error || 'Failed to adjust tokens');
    }
  };

  const handleBulkAction = async () => {
    // Check permissions based on the action
    if (bulkAction === 'suspend' && !canDelete) {
      setBulkDialogError('You don\'t have permission to suspend merchants');
      return;
    }
    if (bulkAction === 'activate' && !canUpdate) {
      setBulkDialogError('You don\'t have permission to activate merchants');
      return;
    }
    if (bulkAction === 'add_credits' && !canCreate) {
      setBulkDialogError('You don\'t have permission to adjust tokens');
      return;
    }

    // Validate token amount is not zero
    if (bulkAction === 'add_credits' && parseInt(bulkCredits) === 0) {
      setBulkDialogError('Token amount cannot be zero');
      return;
    }

    try {
      let successMessage = '';
      switch (bulkAction) {
        case 'suspend':
          await merchantManagementAPI.bulkSuspend(selectedMerchants, bulkReason);
          successMessage = `${selectedMerchants.length} merchants suspended successfully`;
          break;
        case 'activate':
          await merchantManagementAPI.bulkActivate(selectedMerchants);
          successMessage = `${selectedMerchants.length} merchants activated successfully`;
          break;
        case 'add_credits':
          await merchantManagementAPI.bulkAddCredits(selectedMerchants, parseInt(bulkCredits), bulkReason);
          if (parseInt(bulkCredits) > 0) {
            successMessage = `${formatCredits(bulkCredits)} tokens added to ${selectedMerchants.length} merchants successfully`;
          } else {
            successMessage = `${formatCredits(Math.abs(parseInt(bulkCredits)))} tokens deducted from ${selectedMerchants.length} merchants successfully`;
          }
          break;
      }
      setBulkDialog(false);
      setSelectedMerchants([]);
      setBulkAction('');
      setBulkReason('');
      setBulkCredits('');
      setBulkDialogError('');
      loadMerchants();
      loadStats();

      // Show success message
      setError(''); // Clear any previous errors
      setSuccess(successMessage);
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
      setBulkDialogError(error.response?.data?.error || 'Failed to perform bulk action');
    }
  };

  const handleExport = async () => {
    if (!canExport) {
      setError('You don\'t have permission to export data');
      return;
    }

    try {
      // Export based on actually applied filters, not current input values
      const exportParams = {
        search: appliedSearchTerm,
        status: appliedStatusFilter
      };

      const response = await merchantManagementAPI.exportData(exportParams);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Generate filename based on applied filters
      let filename = 'merchants-export';
      if (appliedSearchTerm || appliedStatusFilter) {
        const filterParts = [];
        if (appliedSearchTerm) filterParts.push(`search-${appliedSearchTerm.replace(/[^a-zA-Z0-9]/g, '_')}`);
        if (appliedStatusFilter) filterParts.push(`status-${appliedStatusFilter}`);
        filename += `-${filterParts.join('-')}`;
      } else {
        filename += '-all';
      }
      filename += '.csv';

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      // Show success message with applied filter info
      let successMessage = 'Successfully exported merchants';
      if (appliedSearchTerm || appliedStatusFilter) {
        const filterInfo = [];
        if (appliedSearchTerm) filterInfo.push(`search: "${appliedSearchTerm}"`);
        if (appliedStatusFilter) filterInfo.push(`status: "${appliedStatusFilter}"`);
        successMessage += ` (filtered by ${filterInfo.join(', ')})`;
      } else {
        successMessage += ' (all merchants)';
      }
      setSuccess(successMessage);
    } catch (error) {
      console.error('Failed to export data:', error);
      setError('Failed to export data');
    }
  };



  // Helper to ensure country code has '+' prefix
  const normalizeCode = (code) => {
    if (!code) return '+91';
    const cleanCode = String(code).trim();
    if (!cleanCode) return '+91';
    return cleanCode.startsWith('+') ? cleanCode : `+${cleanCode}`;
  };

  // Validation helper functions
  const validateEmail = (email) => {
    // RFC 5322 compliant email regex that properly handles all valid email characters
    // This regex allows all valid special characters including +, -, ., etc.
    // It follows the official email specification more closely
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(String(email).toLowerCase());
  };

  // Mobile validation function - matches MerchantRegister.js logic
  const validatePhone = (phone, countryId) => {
    if (!phone) return false;

    // For Indian mobile numbers, validate 10 digits starting with 6-9
    if (isIndiaCountry(countryId)) {
      return /^[6-9]\d{9}$/.test(String(phone));
    }

    // For other countries, just check if it's numeric and reasonable length (5-15 digits)
    return /^\d{5,15}$/.test(String(phone));
  };

  const validateWhatsApp = (whatsapp, countryId) => {
    if (!whatsapp) return true; // Optional field, but if sameAsMobile is false it might be required by form
    return validatePhone(whatsapp, countryId);
  };

  // Password validation function - no longer used for merchant creation, but kept for other uses
  // Now, merchants set their own password via email link
  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Must contain at least one digit';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Must contain at least one special character';
    }
    return '';
  };

  // Comprehensive GST validation function
  const validateGSTNumber = (gstNumber) => {
    if (!gstNumber || !gstNumber.trim()) {
      return { isValid: true, message: '' };
    }

    // Remove spaces and convert to uppercase
    const cleanGST = gstNumber.trim().toUpperCase();

    // Check basic structure: 15 characters
    if (cleanGST.length !== 15) {
      return { isValid: false, message: 'GST number must be exactly 15 characters long' };
    }

    // Check format: 2 digits + 5 letters + 4 digits + 1 letter + 1 digit/letter + Z + 1 digit/letter
    const pattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!pattern.test(cleanGST)) {
      return { isValid: false, message: 'Invalid GST number format. Expected format: 27AAPFU0939F1ZV' };
    }

    // Validate state code (first 2 digits)
    const stateCode = cleanGST.substring(0, 2);
    const validStateCodes = [
      '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
      '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37'
    ];
    if (!validStateCodes.includes(stateCode)) {
      return { isValid: false, message: 'Invalid state code in GST number' };
    }

    // Validate PAN format (positions 3-12: 5 letters + 4 digits + 1 letter)
    const panPart = cleanGST.substring(2, 12);
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panPattern.test(panPart)) {
      return { isValid: false, message: 'Invalid PAN format in GST number' };
    }

    // Validate entity code (position 13: 1 digit/letter)
    const entityCode = cleanGST.charAt(12);
    if (!/^[0-9A-Z]$/.test(entityCode)) {
      return { isValid: false, message: 'Invalid entity code in GST number' };
    }

    // Validate default 'Z' character (position 14)
    if (cleanGST.charAt(13) !== 'Z') {
      return { isValid: false, message: 'Invalid character at position 14. Expected \'Z\'' };
    }

    // Validate checksum digit (position 15: 1 digit/letter)
    const checksum = cleanGST.charAt(14);
    if (!/^[0-9A-Z]$/.test(checksum)) {
      return { isValid: false, message: 'Invalid checksum digit in GST number' };
    }

    // Additional validation: Check if PAN is not all same characters
    const panLetters = panPart.substring(0, 5);
    if (new Set(panLetters).size === 1) {
      return { isValid: false, message: 'Invalid PAN: All letters cannot be the same' };
    }

    return { isValid: true, message: 'Valid GST number' };
  };

  // Validate token balance - prevent negative values and enforce maximum allocation limit
  // This ensures the token amount itself is within valid range (0 to MAX_CREDIT_ALLOCATION_LIMIT)
  const validateCredits = (credits) => {
    const numCredits = parseFloat(credits);
    if (isNaN(numCredits)) {
      return { isValid: false, message: 'Tokens must be a valid number' };
    }
    if (numCredits < 0) {
      return { isValid: false, message: 'Token balance cannot be negative' };
    }

    // Check against maximum allocation limit from system settings
    // This only checks the token amount itself, not the total balance
    if (numCredits > maxCreditLimit) {
      return {
        isValid: false,
        message: `Token amount cannot exceed the maximum allocation limit of ${maxCreditLimit.toLocaleString()} tokens`
      };
    }

    return { isValid: true, message: 'Valid token balance' };
  };

  // Validate date range for AI generation history filters
  const validateDateRange = (fromDate, toDate) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);

      if (from > to) {
        return { isValid: false, message: 'From date cannot be greater than to date' };
      }

      if (from > today) {
        return { isValid: false, message: 'From date cannot be in the future' };
      }

      if (to > today) {
        return { isValid: false, message: 'To date cannot be in the future' };
      }
    } else if (fromDate) {
      const from = new Date(fromDate);
      if (from > today) {
        return { isValid: false, message: 'From date cannot be in the future' };
      }
    } else if (toDate) {
      const to = new Date(toDate);
      if (to > today) {
        return { isValid: false, message: 'To date cannot be in the future' };
      }
    }

    return { isValid: true, message: 'Valid date range' };
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    // Required field validations
    if (!merchantForm.name.trim()) {
      errors.name = 'Name is required';
    } else if (merchantForm.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (merchantForm.name.trim().length > 100) {
      errors.name = 'Name cannot exceed 100 characters';
    }

    // Email is optional for WhatsApp-only login
    if (merchantForm.email &&
      merchantForm.email.trim() &&
      merchantForm.email !== 'null' &&
      merchantForm.email !== 'undefined') {
      const trimmedEmail = merchantForm.email.trim();
      if (!validateEmail(trimmedEmail)) {
        errors.email = 'Invalid email format';
      } else {
        // Check if email domain is restricted
        const emailDomain = trimmedEmail.split('@').pop().toLowerCase();
        const isRestricted = restrictedDomains.some(d => d.domain.toLowerCase() === emailDomain && d.is_active);
        if (isRestricted) {
          errors.email = 'Please enter a valid email address';
        }
      }
    }

    // WhatsApp is the only phone field (matches merchant profile); mobile is synced on submit
    if (!merchantForm.whatsapp_number || !merchantForm.whatsapp_number.trim()) {
      errors.whatsapp_number = 'WhatsApp number is required';
    } else if (!merchantForm.country_id) {
      errors.country_id = 'Country is required';
    } else if (!validateWhatsApp(merchantForm.whatsapp_number, merchantForm.country_id)) {
      if (isIndiaCountry(merchantForm.country_id)) {
        errors.whatsapp_number = 'Please enter a valid 10-digit Indian WhatsApp number starting with 6-9';
      } else {
        errors.whatsapp_number = 'Please enter a valid WhatsApp number (5-15 digits)';
      }
    }

    if (!merchantForm.whatsapp_code) {
      errors.whatsapp_code = 'WhatsApp country code is required';
    }

    // Company name validation - now optional
    if (merchantForm.company_name && merchantForm.company_name.trim()) {
      if (merchantForm.company_name.trim().length < 2) {
        errors.company_name = 'Company name must be at least 2 characters';
      } else if (merchantForm.company_name.trim().length > 200) {
        errors.company_name = 'Company name cannot exceed 200 characters';
      }
    }

    // Address validation - now optional
    if (merchantForm.address && merchantForm.address.trim()) {
      // Add any specific address validation if needed
    }

    // Industry and Location - now optional
    // industry_type_id, state_id, city_id removed from mandatory checks

    // Password validation removed - password will be set by merchant via email link
    if (!merchantForm.terms_accepted) {
      errors.terms_accepted = 'Terms acceptance is required';
    }

    // Token balance validation - prevent negative values
    // Tokens are now managed through subscription system
    // No validation needed for legacy credits_balance field in this path

    // GST validation - now optional
    if (merchantForm.gst_number && merchantForm.gst_number.trim()) {
      const gstValidation = validateGSTNumber(merchantForm.gst_number);
      if (!gstValidation.isValid) {
        errors.gst_number = gstValidation.message;
      }
    }

    if (merchantForm.pincode && !/^[0-9]{6}$/.test(merchantForm.pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset form
  const resetForm = () => {
    setGrantFreeTokens(false);
    // Find India country for default values
    const indiaCountry = countries.find(country => isIndiaCountry(country.id));

    setMerchantForm({
      name: '',
      email: '',
      mobile: '',
      mobile_code: indiaCountry ? normalizeCode(indiaCountry.country_code) : '+91',
      company_name: '',
      gst_number: '',
      address: '',
      country_id: indiaCountry ? indiaCountry.id : 2, // Hard default India ID
      state_id: '',
      city_id: '',
      pincode: '',
      // Password field removed - will be set by merchant via email
      credits_balance: 0, // Tokens now managed through subscription system
      is_active: true,
      email_verified: false,
      whatsapp_number: '',
      whatsapp_code: indiaCountry ? normalizeCode(indiaCountry.country_code) : '+91',
      terms_accepted: false,
      original_email_verified: false
    });
    setFormErrors({});
    setFormError(''); // Clear form error
    setStates([]);
    setCities([]);

    // Load states for India if it's the default country
    if (indiaCountry) {
      setTimeout(() => {
        loadStates(indiaCountry.id);
      }, 100);
    }
  };

  // Handle create merchant
  // Add/Edit "Grant free tokens": approves the merchant's free-token verification after save.
  // Same endpoint as the Verify dialog — granted once per account, audited, merchant notified.
  const grantFreeTokensAfterSave = async (merchantId) => {
    if (!grantFreeTokens || !canVerify || !merchantId) return '';
    try {
      const res = await merchantManagementAPI.approveVerification(merchantId, {
        notes: editDialog ? 'Granted from Edit Merchant' : 'Granted from Add Merchant',
      });
      return ` ${res.data.message}`;
    } catch (err) {
      setError(`Merchant saved, but free tokens were not granted: ${err.response?.data?.error || 'please use Verify for Free Tokens'}`);
      return '';
    }
  };

  const renderGrantFreeTokensCheckbox = () => {
    if (!canVerify) return null;
    const status = editDialog ? selectedMerchant?.verification_status : 'pending';
    if (status === 'approved') {
      return (
        <FormControlLabel
          control={<Checkbox checked disabled />}
          label={selectedMerchant?.free_tokens_granted_at ? 'Free tokens granted' : 'Verified for free tokens'}
        />
      );
    }
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={grantFreeTokens}
            onChange={(e) => setGrantFreeTokens(e.target.checked)}
          />
        }
        label="Grant free tokens"
      />
    );
  };

  const handleCreateMerchant = () => {
    if (!canCreate) {
      setError('You don\'t have permission to create merchants');
      return;
    }
    resetForm();
    setCreateDialog(true);
  };

  // Handle edit merchant
  const handleEditMerchant = (merchant) => {
    if (!canUpdate) {
      setError('You don\'t have permission to edit merchants');
      return;
    }

    // Reset error states before setting new data
    setFormErrors({});
    setFormError('');
    setGrantFreeTokens(false);

    // Find country for dial code when API omits whatsapp_code (use country's country_code)
    const merchantCountry = countries.find(country => Number(country.id) === Number(merchant.country_id));

    const whatsappNum = merchant.whatsapp_number || merchant.mobile || '';
    const rawWhatsappCode =
      merchant.whatsapp_code || (merchantCountry ? merchantCountry.country_code : '');
    const whatsappCodeVal = normalizeCode(rawWhatsappCode);

    setMerchantForm({
      name: merchant.name || '',
      email: (merchant.email && merchant.email.trim() !== '' && merchant.email !== 'null' && merchant.email !== 'undefined') ? merchant.email.trim() : '',
      mobile: whatsappNum,
      mobile_code: whatsappCodeVal,
      company_name: merchant.company_name || '',
      gst_number: merchant.gst_number || '',
      address: merchant.address || '',
      country_id: merchant.country_id || '',
      state_id: merchant.state_id || '',
      city_id: merchant.city_id || '',
      pincode: merchant.pincode || '',
      // Password field removed - managed via email setup link
      credits_balance: 0, // Tokens managed through subscription system
      is_active: merchant.is_active !== undefined ? merchant.is_active : true,
      email_verified: merchant.email_verified || false,
      mobile_verified: merchant.mobile_verified || false,
      whatsapp_number: whatsappNum,
      whatsapp_code: whatsappCodeVal,
      terms_accepted: !!(merchant.terms_accepted || merchant.privacy_accepted),
      // Store original verification status to detect changes
      original_email_verified: merchant.email_verified || false
    });

    setSelectedMerchant(merchant);
    setEditDialog(true);

    // Load states and cities if country and state are already selected
    // Use setTimeout to ensure state updates are processed first
    setTimeout(() => {
      if (merchant.country_id) {
        loadStates(merchant.country_id);
        // Load cities after states are loaded
        setTimeout(() => {
          if (merchant.state_id) {
            loadCities(merchant.state_id);
          }
        }, 500);
      }
    }, 100);
  };

  // Handle delete merchant
  const handleDeleteMerchant = (merchant) => {
    if (!canDelete) {
      setError('You don\'t have permission to delete merchants');
      return;
    }
    setSelectedMerchant(merchant);
    setDeleteDialog(true);
  };

  // Mirror WhatsApp to mobile for API; default jewelry industry on create only; omit industry_type_id on edit so DB value is preserved
  const buildMerchantSubmitPayload = (forEdit) => {
    const wn = (merchantForm.whatsapp_number || '').trim();
    const wc = normalizeCode(merchantForm.whatsapp_code);
    const jewelryIndustry = industryTypes.find(
      (type) =>
        type.name?.toLowerCase() === 'jewelry' ||
        type.display_name?.toLowerCase() === 'jewelry'
    );
    const base = {
      ...merchantForm,
      mobile: wn,
      mobile_code: wc,
      whatsapp_number: wn,
      whatsapp_code: wc,
      // One checkbox in admin form maps to both legal consent types.
      terms_accepted: !!merchantForm.terms_accepted,
      privacy_accepted: !!merchantForm.terms_accepted,
      terms_version: TERMS_POLICY_VERSION,
      privacy_version: PRIVACY_POLICY_VERSION
    };
    delete base.password;
    if (forEdit) {
      delete base.original_email_verified;
      delete base.industry_type_id;
    } else if (jewelryIndustry) {
      base.industry_type_id = jewelryIndustry.id;
    }
    return base;
  };

  // Submit create/edit form
  const handleSubmitForm = async () => {
    if (!validateForm()) return;

    try {
      // Check for unique email if email is provided and not blank (edit mode only)
      if (editDialog && merchantForm.email &&
        merchantForm.email.trim() &&
        merchantForm.email !== 'null' &&
        merchantForm.email !== 'undefined') {
        const trimmedEmail = merchantForm.email.trim();
        // Only check uniqueness if email was changed from original
        const originalEmail = merchants.find(m => m.id === selectedMerchant.id)?.email;
        if (trimmedEmail !== originalEmail) {
          try {
            setLoading(true);
            // Use the check-email-availability endpoint from merchant auth API
            const checkResponse = await merchantAuthAPI.checkEmailAvailability(trimmedEmail);
            if (!checkResponse.data.available) {
              setFormErrors({ ...formErrors, email: checkResponse.data.message || 'Email already registered with another merchant' });
              setFormError(checkResponse.data.message || 'Email already registered with another merchant');
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Error checking email uniqueness:', error);
            setFormError('Unable to verify email uniqueness. Please try again.');
            setLoading(false);
            return;
          }
        }
      }

      if (editDialog) {
        // Update existing merchant
        const updateData = buildMerchantSubmitPayload(true);

        // Check if email verification status changed from verified to unverified
        if (merchantForm.original_email_verified && !merchantForm.email_verified) {
          // Show confirmation dialog instead of window.confirm
          setVerificationConfirmDialog(true);
          setPendingFormSubmit(true);
          return; // Stop here and wait for dialog response
        }

        await merchantManagementAPI.update(selectedMerchant.id, updateData);
        setError(''); // Clear any previous errors
        const grantMessage = await grantFreeTokensAfterSave(selectedMerchant.id);
        setEditDialog(false);

        // Show success message
        setSuccess(`Merchant "${updateData.name}" updated successfully.${grantMessage}`);

        // If email verification status changed from unverified to verified, no need for OTP
        // If it changed from verified to unverified, the user was already warned
      } else {
        // Create new merchant without password
        const merchantData = buildMerchantSubmitPayload(false);

        // Create new merchant
        const response = await merchantManagementAPI.create(merchantData);
        const grantMessage = await grantFreeTokensAfterSave(response?.data?.merchant?.id);
        setCreateDialog(false);

        if (response && response.data && response.data.merchant) {
          const createdMerchant = response.data.merchant;
          setSelectedMerchant(createdMerchant);

          // WhatsApp authentication - no password setup needed
          // Show success message with different wording based on whether user is creating or updating
          // For creation: mention WhatsApp login
          setSuccess(`Merchant "${merchantData.name}" created successfully. They can now log in using their WhatsApp number.${grantMessage}`);
        } else {
          // Show success message without extra details if response is not as expected
          setSuccess(`Merchant "${merchantData.name}" created successfully`);
        }
      }
      resetForm();
      loadMerchants();
      loadStats();
    } catch (error) {
      console.error('Failed to save merchant:', error);

      // Check if it's a GST number already registered error
      const errorMessage = error.response?.data?.error || 'Failed to save merchant';
      if (errorMessage.toLowerCase().includes('gst number already registered') ||
        errorMessage.toLowerCase().includes('gst already exists') ||
        errorMessage.toLowerCase().includes('gst already registered') ||
        errorMessage.toLowerCase().includes('gst number is already in use') ||
        errorMessage.toLowerCase().includes('gst number already taken') ||
        errorMessage.toLowerCase().includes('gst number is already taken')) {
        // Set the error in formErrors for GST field
        setFormErrors(prev => ({
          ...prev,
          gst_number: errorMessage
        }));
        setFormError(''); // Clear general form error
      } else if (errorMessage.toLowerCase().includes('email already exists') ||
        errorMessage.toLowerCase().includes('email already registered') ||
        errorMessage.toLowerCase().includes('duplicate email') ||
        errorMessage.toLowerCase().includes('email is already in use') ||
        errorMessage.toLowerCase().includes('email is already taken')) {
        // Set the error in formErrors for email field
        setFormErrors(prev => ({
          ...prev,
          email: errorMessage
        }));
        setFormError(''); // Clear general form error
      } else if (errorMessage.toLowerCase().includes('mobile number already') ||
        errorMessage.toLowerCase().includes('whatsapp number already')) {
        setFormErrors(prev => ({
          ...prev,
          whatsapp_number: errorMessage
        }));
        setFormError('');
      } else if (errorMessage.toLowerCase().includes('duplicate key value') ||
        errorMessage.toLowerCase().includes('unique constraint') ||
        errorMessage.toLowerCase().includes('already exists')) {
        // For other duplicate errors, show as general form error
        setFormError(errorMessage);
        // Clear any field-specific errors
        setFormErrors(prev => ({
          ...prev,
          gst_number: '',
          email: ''
        }));
      } else if (errorMessage.toLowerCase().includes('credits') ||
        errorMessage.toLowerCase().includes('credit allocation') ||
        errorMessage.toLowerCase().includes('maximum credit')) {
        // Map API errors about balance / allocation (server text may still say “credit”).
        if (errorMessage.toLowerCase().includes('credits balance cannot be negative')) {
          setFormErrors(prev => ({
            ...prev,
            credits_balance: errorMessage
          }));
        } else if (errorMessage.toLowerCase().includes('exceeds the maximum credit allocation limit')) {
          setFormErrors(prev => ({
            ...prev,
            credits_balance: errorMessage
          }));
        } else {
          setFormError(errorMessage);
        }
        // Clear other field-specific errors
        setFormErrors(prev => ({
          ...prev,
          gst_number: '',
          email: ''
        }));
      } else {
        // For other errors, show as general form error
        setFormError(errorMessage);
        // Clear any field-specific errors
        setFormErrors(prev => ({
          ...prev,
          gst_number: '',
          email: ''
        }));
      }
    }
  };

  // Handle confirmation of email verification status change
  const handleVerificationConfirmation = async (confirmed) => {
    setVerificationConfirmDialog(false);

    if (confirmed && pendingFormSubmit) {
      // Continue with the form submission
      const updateData = buildMerchantSubmitPayload(true);

      try {
        await merchantManagementAPI.update(selectedMerchant.id, updateData);
        setError(''); // Clear any previous errors
        const grantMessage = await grantFreeTokensAfterSave(selectedMerchant.id);
        setEditDialog(false);
        resetForm();
        loadMerchants();
        loadStats();

        // Show success message
        setSuccess(`Merchant "${updateData.name}" updated successfully with email verification set to ${updateData.email_verified ? 'verified' : 'unverified'}.${grantMessage}`);
      } catch (error) {
        console.error('Failed to update merchant:', error);

        // Check if it's a GST number already registered error
        const errorMessage = error.response?.data?.error || 'Failed to update merchant';
        if (errorMessage.toLowerCase().includes('gst number already registered') ||
          errorMessage.toLowerCase().includes('gst already exists') ||
          errorMessage.toLowerCase().includes('gst already registered') ||
          errorMessage.toLowerCase().includes('gst number is already in use') ||
          errorMessage.toLowerCase().includes('gst number already taken') ||
          errorMessage.toLowerCase().includes('gst number is already taken')) {
          // Set the error in formErrors for GST field
          setFormErrors(prev => ({
            ...prev,
            gst_number: errorMessage
          }));
          setFormError(''); // Clear general form error
        } else if (errorMessage.toLowerCase().includes('email already exists') ||
          errorMessage.toLowerCase().includes('email already registered') ||
          errorMessage.toLowerCase().includes('duplicate email') ||
          errorMessage.toLowerCase().includes('email is already in use') ||
          errorMessage.toLowerCase().includes('email is already taken')) {
          // Set the error in formErrors for email field
          setFormErrors(prev => ({
            ...prev,
            email: errorMessage
          }));
          setFormError(''); // Clear general form error
        } else if (errorMessage.toLowerCase().includes('mobile number already') ||
          errorMessage.toLowerCase().includes('whatsapp number already')) {
          setFormErrors(prev => ({
            ...prev,
            whatsapp_number: errorMessage
          }));
          setFormError('');
        } else if (errorMessage.toLowerCase().includes('duplicate key value') ||
          errorMessage.toLowerCase().includes('unique constraint') ||
          errorMessage.toLowerCase().includes('already exists')) {
          // For other duplicate errors, show as general form error
          setFormError(errorMessage);
          // Clear any field-specific errors
          setFormErrors(prev => ({
            ...prev,
            gst_number: '',
            email: ''
          }));
        } else if (errorMessage.toLowerCase().includes('credits') ||
          errorMessage.toLowerCase().includes('credit allocation') ||
          errorMessage.toLowerCase().includes('maximum credit')) {
          // Map API errors about balance / allocation (server text may still say “credit”).
          if (errorMessage.toLowerCase().includes('credits balance cannot be negative')) {
            setFormErrors(prev => ({
              ...prev,
              credits_balance: errorMessage
            }));
          } else if (errorMessage.toLowerCase().includes('exceeds the maximum credit allocation limit')) {
            setFormErrors(prev => ({
              ...prev,
              credits_balance: errorMessage
            }));
          } else {
            setFormError(errorMessage);
          }
          // Clear other field-specific errors
          setFormErrors(prev => ({
            ...prev,
            gst_number: '',
            email: ''
          }));
        } else {
          // For other errors, show as general form error
          setFormError(errorMessage);
          // Clear any field-specific errors
          setFormErrors(prev => ({
            ...prev,
            gst_number: '',
            email: ''
          }));
        }
      }
    } else {
      // Revert the checkbox change
      setMerchantForm(prev => ({
        ...prev,
        email_verified: prev.original_email_verified
      }));
    }

    setPendingFormSubmit(false);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      const merchantName = selectedMerchant.name;
      const response = await merchantManagementAPI.delete(selectedMerchant.id);
      setDeleteDialog(false);
      setSelectedMerchant(null);
      loadMerchants();
      loadStats();

      // Show success message based on response
      setError(''); // Clear any previous errors

      if (response.data?.user_remains_active) {
        // Dual role user - only merchant roles were deactivated
        const deletedRoles = response.data.deleted_roles || [];
        setSuccess(`Merchant roles deactivated for "${merchantName}". User remains active with admin roles. Deactivated roles: ${deletedRoles.join(', ')}`);
      } else {
        // Pure merchant user - entire user was deleted
        setSuccess(`Merchant "${merchantName}" deleted successfully`);
      }
    } catch (error) {
      console.error('Failed to delete merchant:', error);
      setError('Failed to delete merchant');
    }
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    // Tokens are now managed through subscription system
    // Keep mobile/mobile_code in sync with WhatsApp fields for API payload (no separate mobile row in UI)
    setMerchantForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'whatsapp_code') {
        const code = normalizeCode(value);
        next.whatsapp_code = code;
        next.mobile_code = code;
      }
      if (field === 'whatsapp_number') {
        next.mobile = value;
        next.whatsapp_number = value;
      }
      return next;
    });

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear general form error when user starts typing
    if (formError) {
      setFormError('');
    }
  };

  // Handle sending OTP for email verification
  const handleSendOTP = async () => {
    if (!selectedMerchant || !selectedMerchant.email) {
      setError('No merchant selected or email missing');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      // Using the merchant auth API to resend OTP
      const response = await merchantManagementAPI.sendVerificationOTP(selectedMerchant.id);
      setOtpSuccess('OTP sent successfully to merchant\'s email');
    } catch (error) {
      console.error('Failed to send OTP:', error);
      setOtpError(error.response?.data?.error || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle verifying OTP
  const handleVerifyOTP = async () => {
    if (!otpValue.trim()) {
      setOtpError('Please enter OTP');
      return;
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await merchantManagementAPI.verifyMerchantOTP(selectedMerchant.id, {
        otp: otpValue.trim()
      });

      setOtpSuccess('Email verified successfully!');
      setOtpLoading(false);

      // Close OTP dialog after a delay
      setTimeout(() => {
        setOtpDialog(false);
        setOtpValue('');
        loadMerchants(); // Reload merchants to get updated data
      }, 2000);
    } catch (error) {
      console.error('OTP verification failed:', error);
      setOtpError(error.response?.data?.error || 'Invalid or expired OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle sending password setup link
  const handleSendPasswordLink = async (merchantId) => {
    if (!merchantId) {
      setError('No merchant selected');
      return;
    }

    setPasswordLinkSending(true);

    try {
      await merchantManagementAPI.resendPasswordSetupLink(merchantId);
      setSuccess('Password setup link sent successfully to merchant\'s email');
    } catch (error) {
      console.error('Failed to send password setup link:', error);
      setError(error.response?.data?.error || 'Failed to send password setup link');
    } finally {
      setPasswordLinkSending(false);
    }
  };

  // Handler for AI Generation View dialog - shows both media preview and metadata
  const handleViewAIGeneration = async (generation) => {
    setSelectedGeneration(generation);
    setAiGenerationViewDialog(true);
    setApiLogsLoading(true);
    setApiLogsSummary(null);

    try {
      const response = await merchantManagementAPI.getGenerationApiLogsSummary(generation.id);
      setApiLogsSummary(response.data);
    } catch (error) {
      console.error('Failed to load API logs summary:', error);
      // Don't show error to user, just log it
    } finally {
      setApiLogsLoading(false);
    }
  };


  const handleProcessRefund = async () => {
    if (!canProcessAIGenerationRefund) {
      setError('You don\'t have permission to process refunds');
      return;
    }

    if (!selectedGeneration || !refundReason) {
      setError('Please fill in all required fields');
      return;
    }

    // Always use the full amount for refund
    const amount = selectedGeneration.credits_cost;

    setRefundLoading(true);
    try {
      await merchantManagementAPI.processRefund(selectedGeneration.id, {
        refund_amount: amount,
        refund_reason: refundReason
      });

      setSuccess('Refund processed successfully');
      setRefundDialog(false);
      setRefundAmount('');
      setRefundReason('');

      // Refresh the AI generation history and merchant list
      loadAIGenerationHistory();
      loadMerchants();
      loadStats();

    } catch (error) {
      console.error('Failed to process refund:', error);
      setError(error.response?.data?.error || 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'error';
      case 'inactive': return 'warning';
      case 'deleted': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'suspended': return 'Suspended';
      case 'inactive': return 'Inactive';
      case 'deleted': return 'Deleted';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  };

  // Format date and time for better readability in AI Generation History and notifications.
  // Date order is DD/MM/YYYY (en-GB); time is 12-hour with AM/PM for merchant/admin consistency.
  // Uses toLocaleString with timezone options to ensure consistent Indian time display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // en-GB keeps day/month/year order (DD/MM/YYYY); en-US would show MM/DD/YYYY
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  // Format date as DD/MM/YYYY (day-month-year) for readability
  // Uses toLocaleDateString with timezone options to ensure consistent Indian time display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // en-GB yields DD/MM/YYYY ordering with slash separators
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const handleZoomImage = (imageSrc, imageType) => {
    setZoomedImage({ src: imageSrc, type: imageType });
    setZoomDialog(true);
  };

  const formatBatchId = (batchId) => {
    if (!batchId) return null;

    // If batch ID is short (less than 15 characters), show it as is
    if (batchId.length <= 15) {
      return batchId;
    }

    // For longer batch IDs, show first 10 characters with ellipsis
    const firstPart = batchId.substring(0, 10);
    return `${firstPart}...`;
  };

  // Broadcast Notification functions
  const handleOpenTopicNotification = () => {
    setTopicNotificationForm({
      title: '',
      body: '',
      image_url: '',
      topic_message: '',
      data: {}
    });
    setTopicNotificationError('');
    setTopicNotificationSuccess('');
    setTopicNotificationDialog(true);
  };

  const handleTopicNotificationChange = (field, value) => {
    setTopicNotificationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendTopicNotification = async () => {
    if (!topicNotificationForm.title || !topicNotificationForm.body) {
      setTopicNotificationError('Title and body are required');
      return;
    }

    setTopicNotificationLoading(true);
    setTopicNotificationError('');
    setTopicNotificationSuccess('');

    try {
      const response = await adminNotificationAPI.sendTopicNotification({
        title: topicNotificationForm.title,
        body: topicNotificationForm.body,
        image_url: topicNotificationForm.image_url || undefined,
        topic_message: topicNotificationForm.topic_message || undefined,
        data: topicNotificationForm.data || {}
      });

      const result = response.data;

      if (result.success) {
        setTopicNotificationSuccess('Broadcast notification sent successfully!');
        setTopicNotificationForm({
          title: '',
          body: '',
          image_url: '',
          topic_message: '',
          data: {}
        });

        // Auto-refresh the history table and stats after successful send
        setTimeout(() => {
          loadTopicNotificationHistory();
          loadTopicNotificationStats();
        }, 1000); // Wait 1 second for backend to process

        setTimeout(() => {
          setTopicNotificationDialog(false);
          setTopicNotificationSuccess('');
        }, 2000);
      } else {
        setTopicNotificationError(result.message || 'Failed to send broadcast notification');
      }
    } catch (error) {
      console.error('Error sending topic notification:', error);
      setTopicNotificationError(error.response?.data?.message || 'Failed to send broadcast notification. Please try again.');
    } finally {
      setTopicNotificationLoading(false);
    }
  };

  // Topic Notification History functions
  const loadTopicNotificationHistory = async () => {
    setTopicNotificationHistoryLoading(true);
    setTopicNotificationHistoryError('');

    try {
      const params = {
        page: topicNotificationHistoryPage + 1,
        per_page: topicNotificationHistoryRowsPerPage,
        search: topicNotificationHistorySearchTerm,
        status: topicNotificationHistoryStatusFilter
      };

      const response = await topicNotificationHistoryAPI.getTopicNotificationHistory(params);
      const result = response.data;

      if (result.success) {
        setTopicNotificationHistory(result.data.topic_notifications || []);
        setTopicNotificationHistoryTotal(result.data.pagination.total || 0);
      } else {
        setTopicNotificationHistoryError('Failed to load broadcast notification history');
      }
    } catch (error) {
      console.error('Error loading broadcast notification history:', error);
      setTopicNotificationHistoryError('Failed to load broadcast notification history');
    } finally {
      setTopicNotificationHistoryLoading(false);
    }
  };

  const loadTopicNotificationStats = async () => {
    try {
      const response = await topicNotificationHistoryAPI.getTopicNotificationStats();
      const result = response.data;

      if (result.success) {
        setTopicNotificationHistoryStats(result.data);
      }
    } catch (error) {
      console.error('Error loading topic notification stats:', error);
    }
  };

  const handleViewTopicNotification = (topicNotification) => {
    setSelectedTopicNotification(topicNotification);
    setTopicNotificationViewDialog(true);
  };

  const handleDeleteTopicNotification = (notification) => {
    setNotificationToDelete(notification);
    setDeleteConfirmationDialog(true);
  };

  const confirmDeleteTopicNotification = async () => {
    if (!notificationToDelete) return;

    try {
      const response = await topicNotificationHistoryAPI.deleteTopicNotification(notificationToDelete.topic_id);
      const result = response.data;

      if (result.success) {
        setSuccess('Topic notification deleted successfully');
        // Refresh the history and stats
        loadTopicNotificationHistory();
        loadTopicNotificationStats();
      } else {
        setError('Failed to delete topic notification');
      }
    } catch (error) {
      console.error('Error deleting topic notification:', error);
      setError('Failed to delete topic notification');
    } finally {
      setDeleteConfirmationDialog(false);
      setNotificationToDelete(null);
    }
  };

  const getTopicNotificationStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTopicNotificationStatusLabel = (status) => {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  // Referral Code Management functions

  // Load referral codes - Fixed pagination by consolidating loading logic
  const loadReferralCodes = async () => {
    setReferralCodesLoading(true);
    try {
      const params = {
        page: referralCodesPage + 1,
        per_page: referralCodesRowsPerPage,
        search: referralCodesSearchTerm,
        status: referralCodesStatusFilter
      };

      const response = await referralCodeManagementAPI.getAll(params);

      setReferralCodes(response.data.codes || []);
      setReferralCodesTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load referral codes:', error);
      setError('Failed to load referral codes');
    } finally {
      setReferralCodesLoading(false);
    }
  };

  // Load referral code stats
  const loadReferralCodeStats = async () => {
    try {
      const response = await referralCodeManagementAPI.getStats();
      setReferralCodeStats(response.data);
    } catch (error) {
      console.error('Failed to load referral code stats:', error);
    }
  };

  // Clear referral code filters
  const handleClearReferralCodeFilters = async () => {
    setReferralCodesSearchTerm('');
    setReferralCodesStatusFilter('');
    setReferralCodesPage(0);
    await loadReferralCodes();
  };

  // Assign referral code to a selected merchant
  const handleAssignReferralCode = async () => {
    if (!assignMerchantId) {
      setReferralCodeDialogError('Please select a merchant');
      return;
    }

    setReferralCodeDialogLoading(true);
    setReferralCodeDialogError('');
    setReferralCodeDialogSuccess('');

    try {
      const response = await referralCodeManagementAPI.assignToMerchant(selectedReferralCode.id, assignMerchantId);

      if (response.data.success) {
        setReferralCodeDialogSuccess('Referral code assigned successfully');
        setReferralCodeAssignDialog(false);
        setAssignMerchantId('');
        setSelectedReferralCode(null);

        // Refresh the referral codes list
        setTimeout(() => {
          loadReferralCodes();
          loadReferralCodeStats();
        }, 1000);
      } else {
        setReferralCodeDialogError(response.data.error || 'Failed to assign referral code');
      }
    } catch (error) {
      console.error('Failed to assign referral code:', error);
      setReferralCodeDialogError('Failed to assign referral code');
    } finally {
      setReferralCodeDialogLoading(false);
    }
  };

  // Release referral code from assigned merchant
  const handleReleaseReferralCode = async () => {
    setReferralCodeDialogLoading(true);
    setReferralCodeDialogError('');
    setReferralCodeDialogSuccess('');

    try {
      const response = await referralCodeManagementAPI.releaseFromMerchant(selectedReferralCode.id);

      if (response.data.success) {
        setReferralCodeDialogSuccess('Referral code released successfully');
        setReferralCodeReleaseDialog(false);
        setSelectedReferralCode(null);

        // Refresh the referral codes list
        setTimeout(() => {
          loadReferralCodes();
          loadReferralCodeStats();
        }, 1000);
      } else {
        setReferralCodeDialogError(response.data.error || 'Failed to release referral code');
      }
    } catch (error) {
      console.error('Failed to release referral code:', error);
      setReferralCodeDialogError('Failed to release referral code');
    } finally {
      setReferralCodeDialogLoading(false);
    }
  };





  return (
    <Container maxWidth={false} disableGutters sx={{ mt: 2, mb: 2 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Merchant Management
          </Typography>

        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {/* 5 cards: one row on desktop (20-column grid) */}
      <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 20 }} sx={{ mb: 3 }}>
        <Grid item xs={4} sm={4} md={4}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalance color="primary" sx={{ mr: 2 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4">{stats.total_merchants || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>Total Merchants</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle color="success" sx={{ mr: 2 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4">{stats.active_merchants || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>Active</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning color="warning" sx={{ mr: 2 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4">{stats.suspended_merchants || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>Suspended</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4">{stats.new_this_month || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>New This Month</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <Card elevation={2} sx={{ height: '100%', cursor: 'pointer' }} onClick={showPendingVerification}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <HowToReg sx={{ mr: 2, color: '#d97706' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4">{pendingVerificationCount}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>Pending Verification</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab
            label="Merchants"
            icon={<Business />}
            iconPosition="start"
            sx={{ textTransform: 'capitalize' }}
          />
          <Tab
            label="AI Generation History"
            icon={<AutoAwesome />}
            iconPosition="start"
            sx={{ textTransform: 'capitalize' }}
          />
          <Tab
            label="Broadcast Notifications"
            icon={<Notifications />}
            iconPosition="start"
            sx={{ textTransform: 'capitalize' }}
          />
          <Tab
            label="Referral Codes"
            icon={<QrCode />}
            iconPosition="start"
            sx={{ textTransform: 'capitalize' }}
          />
        </Tabs>
      </Paper>

      {/* Merchants Tab */}
      {activeTab === 0 && (
        <>
          {/* Filters and Search */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            {/* Action Buttons Row */}
            <Box display="flex" gap={2} justifyContent="flex-end" mb={3}>
              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateMerchant}
                  sx={{
                    height: '40px',
                    px: 3,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Add Merchant
                </Button>
              )}
              {canExport && (
                <Tooltip title={`Export merchants based on applied filters${(appliedSearchTerm || appliedStatusFilter) ? ` (${appliedSearchTerm ? `search: "${appliedSearchTerm}"` : ''}${appliedSearchTerm && appliedStatusFilter ? ', ' : ''}${appliedStatusFilter ? `status: "${appliedStatusFilter}"` : ''})` : ' (all merchants)'}`}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExport}
                    sx={{
                      height: '40px',
                      px: 3,
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Export
                  </Button>
                </Tooltip>
              )}
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => { loadMerchants(); loadStats(); }}
                sx={{
                  height: '40px',
                  px: 3,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Refresh
              </Button>
              {selectedMerchants.length > 0 && (
                <>
                  {canDelete && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setBulkAction('suspend');
                        setBulkDialog(true);
                      }}
                      sx={{
                        height: '40px',
                        px: 3,
                        fontWeight: 600,
                        textTransform: 'none'
                      }}
                    >
                      Suspend Selected ({selectedMerchants.length})
                    </Button>
                  )}
                  {canUpdate && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        setBulkAction('activate');
                        setBulkDialog(true);
                      }}
                      sx={{
                        height: '40px',
                        px: 3,
                        fontWeight: 600,
                        textTransform: 'none'
                      }}
                    >
                      Activate Selected ({selectedMerchants.length})
                    </Button>
                  )}
                </>
              )}
            </Box>

            {/* Search and Filter Row */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search merchants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchMerchants();
                    }
                  }}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth variant="outlined" size="medium">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      height: '48px',
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="all">All Merchants (Including Deleted)</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                    <MenuItem value="pending">Pending (email not verified)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="medium">
                  <InputLabel>Free Tokens</InputLabel>
                  <Select
                    value={verificationFilter}
                    label="Free Tokens"
                    onChange={(e) => setVerificationFilter(e.target.value)}
                    sx={{
                      height: '48px',
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending Verification</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
                    onClick={handleSearchMerchants}
                    disabled={loading}
                    size="large"
                    sx={{
                      height: '48px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      flex: 1
                    }}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearMerchantFilters}
                    size="large"
                    sx={{
                      height: '48px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      minWidth: '100px',
                      borderWidth: '2px',
                      '&:hover': {
                        borderWidth: '2px',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    title="Click to clear all filters and show all results"
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Merchants Table */}
          <Paper elevation={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {(canDelete || canUpdate || canCreate) && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedMerchants.length === merchants.length && merchants.length > 0}
                          indeterminate={selectedMerchants.length > 0 && selectedMerchants.length < merchants.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMerchants(merchants.map(m => m.id));
                            } else {
                              setSelectedMerchants([]);
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={adminTableDenseCellSx} sortDirection={sortBy === 'name' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleRequestSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...adminTableDenseCellSx, minWidth: 110 }}>WhatsApp No</TableCell>
                  <TableCell sx={adminTableDenseCellSx} sortDirection={sortBy === 'email' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'email'}
                      direction={sortBy === 'email' ? sortOrder : 'asc'}
                      onClick={() => handleRequestSort('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={adminTableDenseCellSx} sortDirection={sortBy === 'company_name' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'company_name'}
                      direction={sortBy === 'company_name' ? sortOrder : 'asc'}
                      onClick={() => handleRequestSort('company_name')}
                    >
                      Company
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={adminTableDenseCellSx} sortDirection={sortBy === 'credits_balance' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'credits_balance'}
                      direction={sortBy === 'credits_balance' ? sortOrder : 'asc'}
                      onClick={() => handleRequestSort('credits_balance')}
                    >
                      Tokens
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={adminTableDenseCellSx}>Status</TableCell>
                  <TableCell sx={adminTableDenseCellSx}>Free Tokens</TableCell>
                  <TableCell sx={adminTableDenseCellSx} sortDirection={sortBy === 'created_at' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'created_at'}
                      direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                      onClick={() => handleRequestSort('created_at')}
                    >
                      Joined
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={adminTableDenseCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={(canDelete || canUpdate || canCreate) ? 10 : 9}>
                        <LinearProgress />
                      </TableCell>
                    </TableRow>
                  ) : merchants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={(canDelete || canUpdate || canCreate) ? 10 : 9} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No merchants found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    merchants.map((merchant) => (
                      <TableRow key={merchant.id}>
                        {(canDelete || canUpdate || canCreate) && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedMerchants.includes(merchant.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMerchants([...selectedMerchants, merchant.id]);
                                } else {
                                  setSelectedMerchants(selectedMerchants.filter(id => id !== merchant.id));
                                }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={adminTableDenseCellSx}>
                          <Box>
                            <Tooltip title={merchant.name} placement="top" arrow>
                              <Typography variant="subtitle2" sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit' }}>
                                {merchant.name}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              ID: {merchant.id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...adminTableDenseCellSx, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                            {merchant.whatsapp_number ? `${merchant.whatsapp_code || ''} ${merchant.whatsapp_number}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={adminTableDenseCellSx}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {merchant.email_verified ? (
                              <Tooltip title="Email Verified">
                                <VerifiedUser sx={{ fontSize: '1rem' }} color="success" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Email Not Verified">
                                <Email sx={{ fontSize: '1rem' }} color="warning" />
                              </Tooltip>
                            )}
                            <Tooltip title={merchant.email || ''} placement="top" arrow>
                              <Typography variant="body2" sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit' }}>
                                {merchant.email || '-'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={adminTableDenseCellSx}>
                          <Tooltip title={merchant.company_name || ''} placement="top" arrow>
                            <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit' }}>
                              {merchant.company_name || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={adminTableDenseCellSx}>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: 'inherit' }}>
                            {formatCredits(merchant.credits_balance)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={adminTableDenseCellSx}>
                          <Chip
                            label={getStatusLabel(getMerchantStatus(merchant))}
                            color={getStatusColor(getMerchantStatus(merchant))}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={adminTableDenseCellSx}>
                          <VerificationChip status={merchant.verification_status} />
                        </TableCell>
                        <TableCell sx={{ ...adminTableDenseCellSx, whiteSpace: 'nowrap' }}>
                          {merchant.created_at ? formatDate(merchant.created_at) : '-'}
                        </TableCell>
                        <TableCell sx={adminTableDenseCellSx}>
                          <Box display="flex" gap={0.5}>
                            {canView && (
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  sx={adminTableActionIconButtonSx}
                                  onClick={() => handleViewMerchant(merchant)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canUpdate && (
                              <Tooltip title="Edit Merchant">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  sx={adminTableActionIconButtonSx}
                                  onClick={() => handleEditMerchant(merchant)}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canVerify && ['pending', 'rejected'].includes(merchant.verification_status) && (
                              <Tooltip title="Verify for Free Tokens">
                                <IconButton
                                  size="small"
                                  sx={{ ...adminTableActionIconButtonSx, color: '#d97706' }}
                                  onClick={() => setVerifyMerchant(merchant)}
                                >
                                  <HowToReg />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete Merchant">
                                <IconButton
                                  size="small"
                                  color="error"
                                  sx={adminTableActionIconButtonSx}
                                  onClick={() => handleDeleteMerchant(merchant)}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            )}
                            {getMerchantStatus(merchant) === 'suspended' ? (
                              canUpdate && (
                                <Tooltip title="Activate">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={adminTableActionIconButtonSx}
                                    onClick={() => handleActivateMerchant(merchant.id)}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : getMerchantStatus(merchant) === 'deleted' ? (
                              canUpdate && (
                                <Tooltip title="Reactivate">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={adminTableActionIconButtonSx}
                                    onClick={() => handleReactivateMerchant(merchant.id)}
                                  >
                                    <PersonAdd />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : getMerchantStatus(merchant) === 'inactive' ? (
                              canUpdate && (
                                <Tooltip title="Activate">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={adminTableActionIconButtonSx}
                                    onClick={() => handleActivateMerchant(merchant.id)}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : (
                              canDelete && (
                                <Tooltip title="Suspend">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    sx={adminTableActionIconButtonSx}
                                    onClick={() => {
                                      setSelectedMerchant(merchant);
                                      setSuspendDialog(true);
                                    }}
                                  >
                                    <Block />
                                  </IconButton>
                                </Tooltip>
                              )
                            )}
                            {/* {canCreate && (
                          <Tooltip title="Adjust tokens">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedMerchant(merchant);
                                setCreditsDialog(true);
                              }}
                            >
                              <AccountBalanceWallet />
                            </IconButton>
                          </Tooltip>
                        )} */}
                            {/* Email verification icon removed - defaults to True for WhatsApp-only auth */}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </>
      )}

      {/* AI Generation History Tab */}
      {activeTab === 1 && (
        <>


          {/* AI Generation History Filters */}
          {/* Note: Data loads automatically when tab becomes active. Click Search button to apply filters and refresh results. */}
          <Paper elevation={2} sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: 3,
            borderRadius: { xs: 2, sm: 2.5, md: 3 },
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            '& .MuiGrid-container': {
              alignItems: 'stretch'
            },
            '& .MuiFormControl-root': {
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                  borderWidth: '2px'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                  borderWidth: '2px'
                }
              }
            }
          }}>
            {/* First Row */}
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  fullWidth
                  placeholder="Search generations..."
                  value={aiGenerationsSearchTerm}
                  onChange={(e) => setAiGenerationsSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: '40px', sm: '48px', md: '56px' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={aiGenerationsStatusFilter}
                    label="Status"
                    onChange={(e) => setAiGenerationsStatusFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {generationStatuses.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Model Type</InputLabel>
                  <Select
                    value={aiGenerationsModelTypeFilter}
                    label="Model Type"
                    onChange={(e) => setAiGenerationsModelTypeFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Models</MenuItem>
                    {modelTypes.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Output Format</InputLabel>
                  <Select
                    value={aiGenerationsOutputFormatFilter}
                    label="Output Format"
                    onChange={(e) => setAiGenerationsOutputFormatFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Formats</MenuItem>
                    {outputFormats.map((format) => (
                      <MenuItem key={format.id} value={format.id}>
                        {format.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={aiGenerationsCategoryFilter}
                    label="Category"
                    onChange={(e) => setAiGenerationsCategoryFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Second Row */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Clothing Type</InputLabel>
                  <Select
                    value={aiGenerationsClothingTypeFilter}
                    label="Clothing Type"
                    onChange={(e) => setAiGenerationsClothingTypeFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Clothing Types</MenuItem>
                    {clothingTypes.map((ct) => (
                      <MenuItem key={ct.id} value={ct.id}>
                        {ct.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={aiGenerationsGenderFilter}
                    label="Gender"
                    onChange={(e) => setAiGenerationsGenderFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Genders</MenuItem>
                    {genderOptions.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Ethnicity</InputLabel>
                  <Select
                    value={aiGenerationsEthnicityFilter}
                    label="Ethnicity"
                    onChange={(e) => setAiGenerationsEthnicityFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Ethnicities</MenuItem>
                    {ethnicities.map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Bust Color</InputLabel>
                  <Select
                    value={aiGenerationsBustColorFilter}
                    label="Bust Color"
                    onChange={(e) => setAiGenerationsBustColorFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Bust Colors</MenuItem>
                    {bustColors.map((bc) => (
                      <MenuItem key={bc.id} value={bc.id}>
                        {bc.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Third Row */}
            <Grid container spacing={2} alignItems="stretch" sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Bust Finish</InputLabel>
                  <Select
                    value={aiGenerationsBustFinishFilter}
                    label="Bust Finish"
                    onChange={(e) => setAiGenerationsBustFinishFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Bust Finishes</MenuItem>
                    {bustFinishes.map((bf) => (
                      <MenuItem key={bf.id} value={bf.id}>
                        {bf.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Generation Source</InputLabel>
                  <Select
                    value={aiGenerationsSourceFilter}
                    label="Generation Source"
                    onChange={(e) => setAiGenerationsSourceFilter(e.target.value)}
                    sx={{
                      height: { xs: '40px', sm: '48px', md: '56px' },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  >
                    <MenuItem value="">All Sources</MenuItem>
                    <MenuItem value="customer_photo">Customer Photo</MenuItem>
                    <MenuItem value="ai_model_face">AI Model Face</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <TextField
                    value={aiGenerationsBatchIdFilter}
                    label="Batch ID"
                    onChange={(e) => setAiGenerationsBatchIdFilter(e.target.value)}
                    placeholder="Enter batch ID"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '48px', md: '56px' }
                      }
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={aiGenerationsFromDate}
                  onChange={(e) => {
                    setAiGenerationsFromDate(e.target.value);
                    setAiGenerationsDateError(''); // Clear error when date changes
                    // Real-time validation for date range
                    const dateValidation = validateDateRange(e.target.value, aiGenerationsToDate);
                    if (!dateValidation.isValid) {
                      setAiGenerationsDateError(dateValidation.message);
                    }
                  }}
                  error={!!aiGenerationsDateError}
                  helperText={aiGenerationsDateError}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Disable future dates
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: '40px', sm: '48px', md: '56px' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={aiGenerationsToDate}
                  onChange={(e) => {
                    setAiGenerationsToDate(e.target.value);
                    setAiGenerationsDateError(''); // Clear error when date changes
                    // Real-time validation for date range
                    const dateValidation = validateDateRange(aiGenerationsFromDate, e.target.value);
                    if (!dateValidation.isValid) {
                      setAiGenerationsDateError(dateValidation.message);
                    }
                  }}
                  error={!!aiGenerationsDateError}
                  helperText={aiGenerationsDateError}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Disable future dates
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: '40px', sm: '48px', md: '56px' }
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Search and Clear Buttons Row */}
            <Box
              display="flex"
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                mt: 2,
                mb: 3
              }}
            >
              <Button
                variant="contained"
                startIcon={<Search />}
                disabled={!!aiGenerationsDateError}
                onClick={() => {
                  // Validate date range before searching
                  const dateValidation = validateDateRange(aiGenerationsFromDate, aiGenerationsToDate);
                  if (!dateValidation.isValid) {
                    setAiGenerationsDateError(dateValidation.message);
                    return;
                  }

                  setAiGenerationsDateError(''); // Clear any previous errors
                  setAiGenerationsPage(0); // Reset to first page
                  loadAIGenerationHistory(); // Apply all current filters and search
                }}
                sx={{
                  fontSize: { xs: '12px', sm: '13px', md: '14px' },
                  height: { xs: '40px', sm: '48px', md: '56px' },
                  minWidth: { xs: '120px', sm: '140px', md: '160px' },
                  px: { xs: 3, sm: 3.5, md: 4 },
                  py: { xs: 1, sm: 1.5, md: 2 },
                  borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                  fontWeight: 700,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 3px 6px rgba(25, 118, 210, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 12px rgba(25, 118, 210, 0.3)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                title="Click to apply all filters and search for results"
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  // Clear all filters
                  setAiGenerationsSearchTerm('');
                  setAiGenerationsStatusFilter('');
                  setAiGenerationsModelTypeFilter('');
                  setAiGenerationsOutputFormatFilter('');
                  setAiGenerationsCategoryFilter('');
                  setAiGenerationsClothingTypeFilter('');
                  setAiGenerationsGenderFilter('');
                  setAiGenerationsEthnicityFilter('');
                  setAiGenerationsBustColorFilter('');
                  setAiGenerationsBustFinishFilter('');
                  setAiGenerationsSourceFilter('');
                  setAiGenerationsMerchantFilter('');
                  setAiGenerationsBatchIdFilter('');
                  setAiGenerationsFromDate('');
                  setAiGenerationsToDate('');
                  setAiGenerationsDateError('');
                  setAiGenerationsPage(0);
                  loadAIGenerationHistory();
                }}
                sx={{
                  fontSize: { xs: '12px', sm: '13px', md: '14px' },
                  height: { xs: '40px', sm: '48px', md: '56px' },
                  minWidth: { xs: '120px', sm: '140px', md: '160px' },
                  px: { xs: 3, sm: 3.5, md: 4 },
                  py: { xs: 1, sm: 1.5, md: 2 },
                  borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                  fontWeight: 700,
                  textTransform: 'none',
                  borderWidth: '2px',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderWidth: '2px',
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Paper>

          {/* AI Generation History Table */}
          <Paper elevation={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Output Details</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tokens</TableCell>
                    {/* <TableCell>Batch ID</TableCell> */}
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aiGenerationsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <LinearProgress />
                      </TableCell>
                    </TableRow>
                  ) : aiGenerations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No AI generations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    aiGenerations.map((generation) => (
                      <TableRow key={generation.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            #{generation.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{generation.merchant_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {generation.merchant_email}
                            </Typography>
                            {generation.merchant_company && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {generation.merchant_company}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {generation.category_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {generation.model_type_name} - {generation.output_format_name}
                            </Typography>
                            {generation.generation_type === 'batch' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label="Batch"
                                  color="primary"
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {generation.batch_id}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={generation.status_name}
                            color={
                              generation.status_code === 'COMPLETED' ? 'success' :
                                generation.status_code === 'FAILED' ? 'error' :
                                  generation.status_code === 'PROCESSING' ? 'warning' :
                                    generation.status_code === 'CANCELLED' ? 'default' :
                                      'default'
                            }
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {generation.credits_cost ? formatCredits(generation.credits_cost) : '0'}
                          </Typography>
                          {generation.credits_deducted && (
                            <Typography variant="caption" color="success.main" display="block">
                              Deducted
                            </Typography>
                          )}
                          {/* Commented out - hiding refund token information
                          {generation.is_refunded && (
                            <Typography variant="caption" color="error.main" display="block">
                              Refunded: {generation.refund_amount ? formatCredits(generation.refund_amount) : '0'} tokens
                            </Typography>
                          )}
                          */}
                        </TableCell>
                        {/* <TableCell>
                          {generation.batch_id ? (
                            <Tooltip 
                              title={`Full Batch ID: ${generation.batch_id}`}
                              placement="top"
                              arrow
                            >
                              <Chip
                                label={formatBatchId(generation.batch_id)}
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#d97706',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  cursor: 'help'
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell> */}
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(generation.created_at)}
                          </Typography>
                          {generation.processing_completed_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Completed: {formatDateTime(generation.processing_completed_at)}
                            </Typography>
                          )}
                          {/* Commented out - hiding refund date information
                          {generation.refund_date && (
                            <Typography variant="caption" color="error.main" display="block">
                              Refunded: {formatDateTime(generation.refund_date)}
                            </Typography>
                          )}
                          */}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                sx={adminTableActionIconButtonSx}
                                onClick={() => handleViewAIGeneration(generation)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={aiGenerationsTotalCount}
              page={aiGenerationsPage}
              onPageChange={(e, newPage) => setAiGenerationsPage(newPage)}
              rowsPerPage={aiGenerationsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setAiGenerationsRowsPerPage(parseInt(e.target.value, 10));
                setAiGenerationsPage(0);
              }}
              rowsPerPageOptions={[10, 20, 25, 50, 100]}
            />
          </Paper>
        </>
      )}

      {/* Broadcast Notifications Tab */}
      {activeTab === 2 && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Notifications color="primary" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {topicNotificationHistoryStats.total_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Notifications
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#f0fdf4', border: '1px solid #22c55e' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <CheckCircle color="success" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {topicNotificationHistoryStats.sent_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sent Successfully
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Campaign color="warning" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {topicNotificationHistoryStats.broadcast_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Broadcast Messages
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Error color="error" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="error.main">
                        {topicNotificationHistoryStats.failed_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Failed
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Send Notification Section */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Send Broadcast Notification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send notifications to all users subscribed to the configured topic
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Notifications />}
                onClick={handleOpenTopicNotification}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
                  }
                }}
              >
                Send Broadcast Notification
              </Button>
            </Box>

            <Card sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <InfoOutlined color="primary" />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Broadcast Configuration
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Notifications will be sent to all users subscribed to the configured topic.
                </Typography>
              </CardContent>
            </Card>
          </Paper>

          {/* Topic Notification History Section */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight="bold">
                Broadcast Notification History
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => { loadTopicNotificationHistory(); loadTopicNotificationStats(); }}
                disabled={topicNotificationHistoryLoading}
              >
                Refresh
              </Button>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search notifications..."
                  value={topicNotificationHistorySearchTerm}
                  onChange={(e) => setTopicNotificationHistorySearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={topicNotificationHistoryStatusFilter}
                    label="Status Filter"
                    onChange={(e) => setTopicNotificationHistoryStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="sent">Sent</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Error Message */}
            {topicNotificationHistoryError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {topicNotificationHistoryError}
              </Alert>
            )}

            {/* History Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Topic Message</TableCell>
                    <TableCell>Sender</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topicNotificationHistoryLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : topicNotificationHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No topic notifications found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    topicNotificationHistory.map((notification) => (
                      <TableRow key={notification.topic_id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {notification.topic_title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.topic_body.length > 50
                                ? `${notification.topic_body.substring(0, 50)}...`
                                : notification.topic_body}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {notification.topic_message ? (
                            <Chip
                              label={notification.topic_message}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No broadcast message
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {notification.topic_sender_name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTopicNotificationStatusLabel(notification.topic_status)}
                            color={getTopicNotificationStatusColor(notification.topic_status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(notification.topic_created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              sx={adminTableActionIconButtonSx}
                              onClick={() => handleViewTopicNotification(notification)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                            {canDelete && (
                              <IconButton
                                size="small"
                                sx={adminTableActionIconButtonSx}
                                onClick={() => handleDeleteTopicNotification(notification)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={topicNotificationHistoryTotal}
              page={topicNotificationHistoryPage}
              onPageChange={(event, newPage) => setTopicNotificationHistoryPage(newPage)}
              rowsPerPage={topicNotificationHistoryRowsPerPage}
              onRowsPerPageChange={(event) => {
                setTopicNotificationHistoryRowsPerPage(parseInt(event.target.value, 10));
                setTopicNotificationHistoryPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        </>
      )}

      {/* Referral Codes Tab */}
      {activeTab === 3 && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <QrCode color="primary" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {referralCodeStats.total_codes || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Codes
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#f0fdf4', border: '1px solid #22c55e' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <CheckCircle color="success" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {referralCodeStats.assigned_codes || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Assigned Codes
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Inventory color="warning" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {referralCodeStats.available_codes || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Available Codes
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Warning color="error" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="error.main">
                        {referralCodeStats.low_stock_threshold || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Low Stock Alert
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>

          {/* Filters and Search */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search referral codes..."
                  value={referralCodesSearchTerm}
                  onChange={(e) => setReferralCodesSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={referralCodesStatusFilter}
                    label="Status Filter"
                    onChange={(e) => setReferralCodesStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="assigned">Assigned</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={7}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleClearReferralCodeFilters}
                    sx={{
                      fontSize: 13,
                      height: '40px',
                      fontWeight: 600,
                      textTransform: 'none',
                      minWidth: '100px',
                      borderWidth: '2px',
                      '&:hover': {
                        borderWidth: '2px',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    title="Click to clear all filters and show all results"
                  >
                    Clear Filter
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={() => {
                      setReferralCodesPage(0);
                      loadReferralCodes();
                      loadReferralCodeStats();
                    }}
                    sx={{ fontSize: 13 }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Referral Codes Table */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Usage Count</TableCell>
                    <TableCell>Assigned At</TableCell>
                    <TableCell>Created At</TableCell>
                    {/* <TableCell>Actions</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralCodesLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : referralCodes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No referral codes found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    referralCodes.map((code) => (
                      <TableRow key={code.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                            {code.code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isActiveStatus(code.status) ? 'Assigned' : 'Available'}
                            color={isActiveStatus(code.status) ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {code.assigned_merchant ? (
                            <Typography variant="body2">
                              {code.assigned_merchant.email || 'Unknown Email'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not assigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`${code.usage_count || 0} other merchant(s) used this referral code during registration (excluding the owner)`}>
                            <Chip
                              label={code.usage_count || 0}
                              color={code.usage_count > 0 ? 'success' : 'default'}
                              size="small"
                              variant={code.usage_count > 0 ? 'filled' : 'default'}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {code.assigned_at ? formatDateTime(code.assigned_at) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(code.created_at)}
                          </Typography>
                        </TableCell>
                        {/* <TableCell>
                          <Box display="flex" gap={1}>
                            {code.status === 0 ? (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedReferralCode(code);
                                  setReferralCodeAssignDialog(true);
                                }}
                                color="primary"
                                title="Assign to merchant"
                              >
                                <PersonAdd />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedReferralCode(code);
                                  setReferralCodeReleaseDialog(true);
                                }}
                                color="error"
                                title="Release from merchant"
                              >
                                <PersonRemove />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell> */}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={referralCodesTotalCount}
              page={referralCodesPage}
              onPageChange={(event, newPage) => setReferralCodesPage(newPage)}
              rowsPerPage={referralCodesRowsPerPage}
              onRowsPerPageChange={(event) => {
                setReferralCodesRowsPerPage(parseInt(event.target.value, 10));
                setReferralCodesPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Paper>
        </>
      )}

      {/* Verify Merchant Dialog — approve grants the free tokens once */}
      <MerchantVerificationDialog
        open={!!verifyMerchant}
        merchant={verifyMerchant}
        canVerify={canVerify}
        onClose={() => setVerifyMerchant(null)}
        onDone={handleVerificationDone}
      />

      {/* View Merchant Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Business />
            <Typography variant="h5" component="span">
              Merchant Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedMerchant && (
            <Box sx={{ p: 3 }}>
              {/* Header Card with Main Info */}
              <Card
                sx={{
                  mb: 3,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: 1
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {selectedMerchant.name?.charAt(0)?.toUpperCase() || 'M'}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                          {selectedMerchant.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          ID: {selectedMerchant.id}
                        </Typography>
                      </Box>
                    </Box>
                    <Box textAlign="right">
                      <Chip
                        label={getStatusLabel(getMerchantStatus(selectedMerchant))}
                        color={getStatusColor(getMerchantStatus(selectedMerchant))}
                        size="large"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          px: 2,
                          py: 1
                        }}
                        icon={getMerchantStatus(selectedMerchant) === 'suspended' ? <Warning /> :
                          getMerchantStatus(selectedMerchant) === 'inactive' ? <Block /> : <CheckCircle />}
                      />
                    </Box>
                  </Box>

                  {/* Account verification — free tokens are granted only after approval */}
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                    <Typography variant="body2" fontWeight={600}>Free Tokens:</Typography>
                    <VerificationChip status={selectedMerchant.verification_status} />
                    {selectedMerchant.verification_reviewed_by_name && (
                      <Typography variant="body2" color="text.secondary">
                        by {selectedMerchant.verification_reviewed_by_name} on{' '}
                        {formatDateTimeIST(selectedMerchant.verification_reviewed_at)}
                      </Typography>
                    )}
                    {selectedMerchant.verification_rejection_reason && (
                      <Typography variant="body2" color="error">
                        Reason: {selectedMerchant.verification_rejection_reason}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Free tokens: {selectedMerchant.free_tokens_granted_at
                        ? `granted ${formatDateTimeIST(selectedMerchant.free_tokens_granted_at)}` : 'not granted'}
                    </Typography>
                    {canVerify && ['pending', 'rejected'].includes(selectedMerchant.verification_status) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<HowToReg />}
                        onClick={() => setVerifyMerchant(selectedMerchant)}
                      >
                        Verify for Free Tokens
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                {/* Basic Information Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', boxShadow: 1 }}>
                    <CardHeader
                      title="Basic Information"
                      avatar={<Business sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        pb: 1,
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText'
                      }}
                    />
                    <CardContent>
                      <Box mb={2}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Email color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Email Address
                          </Typography>
                          {selectedMerchant.email_verified ? (
                            <Tooltip title="Email Verified">
                              <VerifiedUser fontSize="small" color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Email Not Verified">
                              <Email fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="body1" fontWeight="500">
                            {selectedMerchant.email || '-'}
                          </Typography>
                          {/* Verify Email button removed - defaults to True for WhatsApp-only auth */}
                        </Box>
                      </Box>

                      <Box mb={2}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Business color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Company Name
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="500">
                          {selectedMerchant.company_name || '-'}
                        </Typography>
                      </Box>

                      <Box mb={2}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Phone color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Mobile Number
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="500">
                          {selectedMerchant.mobile ? `${selectedMerchant.mobile_code || ''} ${selectedMerchant.mobile}` : '-'}
                        </Typography>
                      </Box>

                      <Box mb={2}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <WhatsApp sx={{ color: '#25D366' }} fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            WhatsApp Number
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="500">
                          {selectedMerchant.whatsapp_number ?
                            `${selectedMerchant.whatsapp_code || selectedMerchant.mobile_code} ${selectedMerchant.whatsapp_number}` :
                            '-'
                          }
                        </Typography>
                      </Box>

                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <AccountBalance color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            GST Number
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="500">
                          {selectedMerchant.gst_number || '-'}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Description color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Legal Consent
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="500">
                          Terms: {selectedMerchant.terms_accepted ? 'Accepted' : 'Not available'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Version: {selectedMerchant.terms_version || '-'} | At: {selectedMerchant.terms_accepted_at ? formatDateTime(selectedMerchant.terms_accepted_at) : '-'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 1 }}>
                          Privacy: {selectedMerchant.privacy_accepted ? 'Accepted' : 'Not available'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Version: {selectedMerchant.privacy_version || '-'} | At: {selectedMerchant.privacy_accepted_at ? formatDateTime(selectedMerchant.privacy_accepted_at) : '-'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Tokens overview card (values from credits_* API fields) */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', boxShadow: 1 }}>
                    <CardHeader
                      title="Tokens overview"
                      avatar={<TrendingUp sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        pb: 1,
                        backgroundColor: 'success.light',
                        color: 'success.contrastText'
                      }}
                    />
                    <CardContent>
                      <Box mb={3}>
                        <Typography variant="h3" fontWeight="bold" color="success.main" textAlign="center">
                          {selectedMerchant.credits_balance ? formatCredits(selectedMerchant.credits_balance) : '0'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          Current Balance
                        </Typography>
                      </Box>

                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            Total Requested
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary.main">
                            {selectedMerchant.total_credits_purchased ? formatCredits(selectedMerchant.total_credits_purchased) : '0'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            Total Used
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="error.main">
                            {selectedMerchant.total_credits_used ? formatCredits(selectedMerchant.total_credits_used) : '0'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Referral Code Information Card */}
                {/* 
                  This card displays referral code information for the merchant:
                  - Left side: Referral code used during registration (from merchant.referral_code)
                  - Right side: Merchant's own assigned referral code (from merchant.referral_code_info.assigned_code)
                  - Additional details section shows comprehensive referral code information
                */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: 1 }}>
                    <CardHeader
                      title="Referral Code Information"
                      avatar={<QrCode sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        pb: 1,
                        backgroundColor: 'warning.light',
                        color: 'warning.contrastText'
                      }}
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        {/* Referral Code Used During Registration */}
                        <Grid item xs={12} md={6}>
                          <Box textAlign="center">
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: 'warning.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                margin: '0 auto 16px',
                                fontSize: '1.5rem'
                              }}
                            >
                              <QrCode />
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="warning.main">
                              {selectedMerchant.referral_code || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedMerchant.referral_code ? 'Referral Code Used' : 'No Referral Code Used'}
                            </Typography>
                            {selectedMerchant.referral_code && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Joined using this referral code
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Merchant's Own Referral Code */}
                        <Grid item xs={12} md={6}>
                          <Box textAlign="center">
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: 'success.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                margin: '0 auto 16px',
                                fontSize: '1.5rem'
                              }}
                            >
                              <Campaign />
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {selectedMerchant.referral_code_info?.assigned_code || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedMerchant.referral_code_info?.has_assigned_code ? 'Own Referral Code' : 'No Referral Code Assigned'}
                            </Typography>
                            {selectedMerchant.referral_code_info?.has_assigned_code && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Can share with others
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Additional Referral Code Details */}
                      {(selectedMerchant.referral_code || selectedMerchant.referral_code_info?.has_assigned_code) && (
                        <Box sx={{ mt: 3, p: 2, backgroundColor: '#fef3c7', borderRadius: 2, border: '1px solid #f59e0b' }}>
                          <Typography variant="subtitle2" color="#92400e" fontWeight="bold" gutterBottom>
                            Referral Code Details
                          </Typography>

                          <Grid container spacing={2}>
                            {selectedMerchant.referral_code && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="#92400e">
                                  <strong>Code Used During Registration:</strong> {selectedMerchant.referral_code}
                                </Typography>
                              </Grid>
                            )}
                            {selectedMerchant.referral_code_info?.has_assigned_code && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="#92400e">
                                  <strong>Own Referral Code:</strong> {selectedMerchant.referral_code_info.assigned_code}
                                </Typography>
                              </Grid>
                            )}
                            {selectedMerchant.referral_code && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="#92400e">
                                  <strong>Registration Date:</strong> {formatDate(selectedMerchant.created_at)}
                                </Typography>
                              </Grid>
                            )}
                            {selectedMerchant.referral_code_info?.assigned_at && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="#92400e">
                                  <strong>Code Assigned On:</strong> {formatDate(selectedMerchant.referral_code_info.assigned_at)}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                          {selectedMerchant.referral_code && (
                            <Typography variant="caption" color="#92400e" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                              This merchant joined the platform using the referral code "{selectedMerchant.referral_code}"
                            </Typography>
                          )}
                          {selectedMerchant.referral_code_info?.has_assigned_code && (
                            <Typography variant="caption" color="#92400e" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                              {selectedMerchant.referral_code ? 'Additionally, ' : ''}This merchant has been assigned their own referral code "{selectedMerchant.referral_code_info.assigned_code}" to share with others
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Activity Information Card */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: 1 }}>
                    <CardHeader
                      title="Activity & Timeline"
                      avatar={<CalendarToday sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        pb: 1,
                        backgroundColor: 'info.light',
                        color: 'info.contrastText'
                      }}
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Box textAlign="center">
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                margin: '0 auto 16px',
                                fontSize: '1.5rem'
                              }}
                            >
                              <CalendarToday />
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                              {selectedMerchant.created_at ? formatDate(selectedMerchant.created_at) : '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Member Since
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box textAlign="center">
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: 'success.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                margin: '0 auto 16px',
                                fontSize: '1.5rem'
                              }}
                            >
                              <CheckCircle />
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {selectedMerchant.last_login_at ? formatDate(selectedMerchant.last_login_at) : '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Last Login
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box textAlign="center">
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: 'secondary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                margin: '0 auto 16px',
                                fontSize: '1.5rem'
                              }}
                            >
                              <TrendingUp />
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="secondary.main">
                              {selectedMerchant.last_generation_at ? formatDate(selectedMerchant.last_generation_at) : '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Last Generation
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: 'grey.50' }}>
          <Button
            onClick={() => setViewDialog(false)}
            variant="contained"
            size="large"
            color="primary"
            sx={{ px: 4 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Merchant Dialog */}
      <Dialog open={suspendDialog} onClose={() => setSuspendDialog(false)}>
        <DialogTitle>Suspend Merchant</DialogTitle>
        <DialogContent>
          {suspendDialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSuspendDialogError('')}>
              {suspendDialogError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Reason for suspension"
            value={suspendReason}
            onChange={(e) => {
              setSuspendReason(e.target.value);
              setSuspendDialogError(''); // Clear error when user types
            }}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSuspendDialog(false);
            setSuspendDialogError('');
            setSuspendReason('');
          }}>Cancel</Button>
          <Button onClick={handleSuspendMerchant} color="error" variant="contained">
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust token balance dialog */}
      <Dialog open={creditsDialog} onClose={() => setCreditsDialog(false)}>
        <DialogTitle>Adjust tokens</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Enter a positive number to add tokens or a negative number to deduct tokens.
          </Typography>

          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
              {dialogError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Token amount"
            type="number"
            value={creditsAmount}
            onChange={(e) => {
              setCreditsAmount(e.target.value);
              setDialogError(''); // Clear error when user types
            }}

            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason"
            value={creditsReason}
            onChange={(e) => setCreditsReason(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreditsDialog(false);
            setDialogError('');
            setCreditsAmount('');
            setCreditsReason('');
          }}>Cancel</Button>
          <Button
            onClick={handleAddCredits}
            color={parseInt(creditsAmount) >= 0 ? "primary" : "error"}
            variant="contained"
          >
            {parseInt(creditsAmount) >= 0 ? "Add tokens" : "Deduct tokens"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)}>
        <DialogTitle>
          {bulkAction === 'suspend' && 'Suspend Selected Merchants'}
          {bulkAction === 'activate' && 'Activate Selected Merchants'}
          {bulkAction === 'add_credits' && 'Adjust tokens for selected merchants'}
        </DialogTitle>
        <DialogContent>
          {bulkDialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBulkDialogError('')}>
              {bulkDialogError}
            </Alert>
          )}

          {bulkAction === 'suspend' && (
            <TextField
              fullWidth
              label="Reason for suspension"
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          )}
          {bulkAction === 'add_credits' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Enter a positive number to add tokens or a negative number to deduct tokens.
              </Typography>
              <TextField
                fullWidth
                label="Token amount"
                type="number"
                value={bulkCredits}
                onChange={(e) => {
                  setBulkCredits(e.target.value);
                  setBulkDialogError(''); // Clear error when user types
                }}
                helperText="Use negative value to deduct tokens"
                sx={{ mt: 1, mb: 2 }}
              />
              <TextField
                fullWidth
                label="Reason"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                multiline
                rows={3}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBulkDialog(false);
            setBulkDialogError('');
            setBulkAction('');
            setBulkReason('');
            setBulkCredits('');
          }}>Cancel</Button>
          <Button
            onClick={handleBulkAction}
            color={
              bulkAction === 'suspend' ? 'error' :
                (bulkAction === 'add_credits' && parseInt(bulkCredits) < 0) ? 'error' :
                  'primary'
            }
            variant="contained"
          >
            {bulkAction === 'suspend' && 'Suspend'}
            {bulkAction === 'activate' && 'Activate'}
            {bulkAction === 'add_credits' && parseInt(bulkCredits) >= 0 && 'Add tokens'}
            {bulkAction === 'add_credits' && parseInt(bulkCredits) < 0 && 'Deduct tokens'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Merchant Dialog */}
      <Dialog open={createDialog} onClose={() => {
        setCreateDialog(false);
        setFormError('');
        setFormErrors({});
      }} fullWidth maxWidth="md">
        <DialogTitle>Add New Merchant</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Name"
              value={merchantForm.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Email"
              type="email"
              value={merchantForm.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              placeholder="Enter merchant email address (optional)"
            />
            {/* Password field removed - WhatsApp authentication only */}
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <AlertTitle>WhatsApp Authentication</AlertTitle>
              Merchant can log in using their WhatsApp number directly. No password setup required.
            </Alert>
            {/* WhatsApp only (matches merchant profile); mobile/mobile_code are synced on submit */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              WhatsApp Number (Primary Login Method)
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required error={!!formErrors.whatsapp_code}>
                  <InputLabel>WhatsApp Country Code</InputLabel>
                  <Select
                    value={merchantForm.whatsapp_code || merchantForm.mobile_code || '+91'}
                    onChange={(e) => handleFormChange('whatsapp_code', e.target.value)}
                    label="WhatsApp Country Code"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.country_code}>
                        {country.country_code} - {country.country_slug}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.whatsapp_code && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      {formErrors.whatsapp_code}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  required
                  fullWidth
                  label="WhatsApp Number"
                  value={merchantForm.whatsapp_number || ''}
                  onChange={(e) => handleFormChange('whatsapp_number', e.target.value)}
                  error={!!formErrors.whatsapp_number}
                  helperText={formErrors.whatsapp_number || 'Primary login method for merchant'}
                  placeholder="Enter WhatsApp number"
                  inputProps={{ maxLength: 15 }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ m: 0 }}
                />
              </Grid>
            </Grid>

            <TextField
              margin="normal"
              fullWidth
              label="Company Name"
              value={merchantForm.company_name}
              onChange={(e) => handleFormChange('company_name', e.target.value)}
              error={!!formErrors.company_name}
              helperText={formErrors.company_name}
            />
            <TextField
              margin="normal"
              fullWidth
              label="GST Number"
              value={merchantForm.gst_number}
              onChange={(e) => handleFormChange('gst_number', e.target.value)}
              placeholder="27AAPFU0939F1ZV"
              error={!!formErrors.gst_number}
              helperText={formErrors.gst_number || "Enter your 15-digit GSTIN (e.g. 27AAPFU0939F1ZV)"}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Address"
              value={merchantForm.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />

            {/* Country Dropdown */}
            <FormControl fullWidth margin="normal" error={!!formErrors.country_id}>
              <InputLabel>Country</InputLabel>
              <Select
                value={merchantForm.country_id}
                onChange={handleCountryChange}
                label="Country"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.country_name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.country_id && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {formErrors.country_id}
                </Typography>
              )}
            </FormControl>

            {/* State Dropdown */}
            <FormControl fullWidth margin="normal" error={!!formErrors.state_id}>
              <InputLabel>State</InputLabel>
              <Select
                value={merchantForm.state_id}
                onChange={handleStateChange}
                label="State"
                disabled={!merchantForm.country_id || loadingStates}
              >
                {loadingStates ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading states...
                  </MenuItem>
                ) : (
                  states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.state_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.state_id && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {formErrors.state_id}
                </Typography>
              )}
            </FormControl>

            {/* City Dropdown */}
            <FormControl
              fullWidth
              margin="normal"
              error={!!formErrors.city_id}
              className={formErrors.city_id ? 'city-input-error' : ''}
            >
              <InputLabel>City</InputLabel>
              <Select
                value={merchantForm.city_id}
                onChange={handleCityChange}
                label="City"
                disabled={!merchantForm.state_id || loadingCities}
              >
                {loadingCities ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading cities...
                  </MenuItem>
                ) : (
                  cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.city_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.city_id && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {formErrors.city_id}
                </Typography>
              )}
            </FormControl>

            <TextField
              margin="normal"
              fullWidth
              label="Pincode"
              value={merchantForm.pincode}
              onChange={(e) => handleFormChange('pincode', e.target.value)}
              error={!!formErrors.pincode}
              helperText={formErrors.pincode}
            />
            <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
              <AlertTitle>Subscription System</AlertTitle>
              New merchants automatically receive a <strong>Starter Package</strong> with 2000 tokens valid for 30 days.
              Tokens are now managed through the subscription system.
            </Alert>
            {/* Account Status Checkboxes - Single Row */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Account Status
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={merchantForm.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active Account"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={merchantForm.mobile_verified}
                      onChange={(e) => handleFormChange('mobile_verified', e.target.checked)}
                    />
                  }
                  label="Mobile Verified"
                />
                {/* Email verified checkbox removed - defaults to True for WhatsApp-only auth */}
                {renderGrantFreeTokensCheckbox()}
              </Box>
            </Box>
            <Box sx={{ mt: 1, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!merchantForm.terms_accepted}
                    onChange={(e) => handleFormChange('terms_accepted', e.target.checked)}
                  />
                }
                label="I confirm this merchant accepted Terms and Privacy Policy"
              />
              {formErrors.terms_accepted && (
                <Typography variant="caption" color="error" display="block">
                  {formErrors.terms_accepted}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialog(false);
            setFormError('');
            setFormErrors({});
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitForm}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Merchant Dialog */}
      <Dialog open={editDialog} onClose={() => {
        setEditDialog(false);
        setFormError('');
        setFormErrors({});
      }} fullWidth maxWidth="md">
        <DialogTitle>Edit Merchant</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Name"
              value={merchantForm.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Email"
              type="email"
              value={merchantForm.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              placeholder="Enter merchant email address (optional)"
              disabled={true}
            />
            {/* Password field removed from edit form - WhatsApp authentication only */}
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <AlertTitle>WhatsApp Authentication</AlertTitle>
              Merchant uses WhatsApp number for login. No password management needed.
            </Alert>
            {/* WhatsApp only (matches merchant profile); mobile/mobile_code are synced on submit */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              WhatsApp Number (Primary Login Method)
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required error={!!formErrors.whatsapp_code}>
                  <InputLabel>WhatsApp Country Code</InputLabel>
                  <Select
                    value={merchantForm.whatsapp_code || merchantForm.mobile_code || '+91'}
                    onChange={(e) => handleFormChange('whatsapp_code', e.target.value)}
                    label="WhatsApp Country Code"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.country_code}>
                        {country.country_code} - {country.country_slug}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.whatsapp_code && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      {formErrors.whatsapp_code}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  required
                  fullWidth
                  label="WhatsApp Number"
                  value={merchantForm.whatsapp_number || ''}
                  onChange={(e) => handleFormChange('whatsapp_number', e.target.value)}
                  error={!!formErrors.whatsapp_number}
                  helperText={formErrors.whatsapp_number || 'Primary login method for merchant'}
                  placeholder="Enter WhatsApp number"
                  inputProps={{ maxLength: 15 }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ m: 0 }}
                />
              </Grid>
            </Grid>

            <TextField
              margin="normal"
              fullWidth
              label="Company Name"
              value={merchantForm.company_name}
              onChange={(e) => handleFormChange('company_name', e.target.value)}
              error={!!formErrors.company_name}
              helperText={formErrors.company_name}
            />
            <TextField
              margin="normal"
              fullWidth
              label="GST Number"
              value={merchantForm.gst_number}
              onChange={(e) => handleFormChange('gst_number', e.target.value)}
              placeholder="e.g. 27AAPFU0939F1ZV"
              error={!!formErrors.gst_number}
              helperText={formErrors.gst_number || "Enter your 15-digit GSTIN (e.g., 27AAPFU0939F1ZV)"}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Address"
              value={merchantForm.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />

            {/* Country Dropdown */}
            <FormControl fullWidth margin="normal" error={!!formErrors.country_id}>
              <InputLabel>Country</InputLabel>
              <Select
                value={merchantForm.country_id}
                onChange={handleCountryChange}
                label="Country"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.country_name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.country_id && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {formErrors.country_id}
                </Typography>
              )}
            </FormControl>

            {/* State Dropdown */}
            <FormControl fullWidth margin="normal" error={!!formErrors.state_id}>
              <InputLabel>State</InputLabel>
              <Select
                value={merchantForm.state_id}
                onChange={handleStateChange}
                label="State"
                disabled={!merchantForm.country_id || loadingStates}
              >
                {loadingStates ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading states...
                  </MenuItem>
                ) : (
                  states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.state_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.state_id && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {formErrors.state_id}
                </Typography>
              )}
            </FormControl>

            {/* City Dropdown */}
            <FormControl
              fullWidth
              margin="normal"
              error={!!formErrors.city_id}
              className={formErrors.city_id ? 'city-input-error' : ''}
            >
              <InputLabel>City</InputLabel>
              <Select
                value={merchantForm.city_id}
                onChange={handleCityChange}
                label="City"
                disabled={!merchantForm.state_id || loadingCities}
              >
                {loadingCities ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading cities...
                  </MenuItem>
                ) : (
                  cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.city_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.city_id && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {formErrors.city_id}
                </Typography>
              )}
            </FormControl>

            <TextField
              margin="normal"
              fullWidth
              label="Pincode"
              value={merchantForm.pincode}
              onChange={(e) => handleFormChange('pincode', e.target.value)}
              error={!!formErrors.pincode}
              helperText={formErrors.pincode}
            />
            <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
              <AlertTitle>Subscription System</AlertTitle>
              Tokens are managed through the subscription system. Use the Subscription Management module to adjust tokens.
            </Alert>
            {/* Account Status Checkboxes - Single Row */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Account Status
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={merchantForm.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active Account"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={merchantForm.mobile_verified}
                      onChange={(e) => handleFormChange('mobile_verified', e.target.checked)}
                    />
                  }
                  label="Mobile Verified"
                />
                {/* Email verified checkbox removed - defaults to True for WhatsApp-only auth */}
                {renderGrantFreeTokensCheckbox()}
              </Box>
            </Box>
            <Box sx={{ mt: 1, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!merchantForm.terms_accepted}
                    onChange={(e) => handleFormChange('terms_accepted', e.target.checked)}
                  />
                }
                label="I confirm this merchant accepted Terms and Privacy Policy"
              />
              {formErrors.terms_accepted && (
                <Typography variant="caption" color="error" display="block">
                  {formErrors.terms_accepted}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialog(false);
            setFormError('');
            setFormErrors({});
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitForm}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete merchant: {selectedMerchant?.name}?</Typography>
          <Typography variant="body2" color="text.secondary">
            This action will mark the merchant as inactive and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog open={otpDialog} onClose={() => setOtpDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          pb: 2,
          backgroundColor: 'info.main',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Email />
            <Typography variant="h6" component="span">
              Email Verification
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedMerchant && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Verify email for merchant: <strong>{selectedMerchant.name}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Email: <strong>{selectedMerchant.email}</strong>
                </Typography>
              </Alert>

              {otpSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {otpSuccess}
                </Alert>
              )}

              {otpError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {otpError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Enter OTP"
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value)}
                disabled={otpLoading}
                sx={{ mb: 2 }}
                placeholder="Enter 4-digit OTP sent to merchant's email"
              />

              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleSendOTP}
                  disabled={otpLoading}
                  startIcon={otpLoading ? <CircularProgress size={20} /> : <Email />}
                >
                  {otpLoading ? 'Sending...' : 'Resend OTP'}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVerifyOTP}
                  disabled={otpLoading || !otpValue.trim()}
                  startIcon={otpLoading ? <CircularProgress size={20} /> : <VerifiedUser />}
                >
                  {otpLoading ? 'Verifying...' : 'Verify OTP'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: 'grey.50' }}>
          <Button
            onClick={() => setOtpDialog(false)}
            variant="outlined"
            sx={{ px: 3 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Email Verification Status Change */}
      <Dialog
        open={verificationConfirmDialog}
        onClose={() => handleVerificationConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', py: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning />
            <Typography variant="h6">Confirm Email Verification Change</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will require the merchant to verify their email again.
          </Alert>
          <Typography variant="body1" gutterBottom>
            You are about to change the email verification status from <strong>Verified</strong> to <strong>Unverified</strong> for:
          </Typography>
          <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body1">
              <strong>Name:</strong> {merchantForm?.name}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {merchantForm?.email}
            </Typography>
          </Box>
          <Typography variant="body1" color="error">
            This means the merchant will need to complete email verification again to access their account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => handleVerificationConfirmation(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleVerificationConfirmation(true)}
            color="warning"
            variant="contained"
            startIcon={<Warning />}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generation View Dialog */}
      <Dialog
        open={aiGenerationViewDialog}
        onClose={() => setAiGenerationViewDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <AutoAwesome />
            <Typography variant="h5" component="span">
              AI Generation Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGeneration && (
            <Box sx={{ p: 3 }}>
              {/* Header Card with Main Info */}
              <Card
                sx={{
                  mb: 3,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: 1
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        #{selectedGeneration.id}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                          Generation #{selectedGeneration.id}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {selectedGeneration.model_type_name} • {selectedGeneration.output_format_name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box textAlign="right">
                      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                        <Chip
                          label={selectedGeneration.status_name}
                          color={
                            selectedGeneration.status_code === 'COMPLETED' ? 'success' :
                              selectedGeneration.status_code === 'FAILED' ? 'error' :
                                selectedGeneration.status_code === 'PROCESSING' ? 'warning' :
                                  'default'
                          }
                          size="large"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            px: 2,
                            py: 1
                          }}
                        />
                        {!selectedGeneration.is_refunded && selectedGeneration.credits_deducted && canProcessAIGenerationRefund && (
                          <Chip
                            label="Refund Available"
                            color="warning"
                            size="small"
                            icon={<AccountBalanceWallet sx={{ fontSize: 16 }} />}
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #f59e0b'
                            }}
                          />
                        )}
                        {selectedGeneration.is_refunded && (
                          <Chip
                            label={`Refunded: ${selectedGeneration.refund_amount ? formatCredits(selectedGeneration.refund_amount) : '0'} tokens`}
                            color="error"
                            size="small"
                            icon={<AccountBalanceWallet sx={{ fontSize: 16 }} />}
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                              border: '1px solid #ef4444'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                {/* Reels show their selected source clips (image or video) as inputs,
                    instead of the first clip's uploaded/enhanced image. */}
                {selectedGeneration.is_reel ? (
                  (selectedGeneration.reel_sources && selectedGeneration.reel_sources.length) ? (
                    <>
                      {/* Source clips shown as a single-card carousel: one clip at a time
                          with prev/next arrows and dot navigation. */}
                      <Grid item xs={12} md={6}>
                        <Card sx={adminMediaPreviewCardSx}>
                          <CardHeader
                            title={`Source Clips (${(reelClipIndex % selectedGeneration.reel_sources.length) + 1} / ${selectedGeneration.reel_sources.length})`}
                            avatar={<Movie sx={{ color: '#fff' }} />}
                            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', sx: adminMediaPreviewHeaderTitleSx }}
                            sx={{ ...adminMediaPreviewCardHeaderBarSx, background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)', color: '#fff' }}
                          />
                          <CardContent sx={{ flexGrow: 1 }}>
                            {(() => {
                              const clips = selectedGeneration.reel_sources;
                              const active = ((reelClipIndex % clips.length) + clips.length) % clips.length;
                              const src = clips[active];
                              return (
                                <>
                                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {clips.length > 1 && (
                                      <IconButton
                                        onClick={() => setReelClipIndex((i) => (((i - 1) % clips.length) + clips.length) % clips.length)}
                                        sx={{ position: 'absolute', left: 4, zIndex: 6, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' }, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                                      >
                                        <ChevronLeft />
                                      </IconButton>
                                    )}
                                    <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                      <Chip
                                        size="small"
                                        label={`${active + 1}  ·  ${src.is_video ? 'VIDEO' : 'IMAGE'}`}
                                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 6, height: 22, fontSize: 10, fontWeight: 700, color: '#fff', bgcolor: src.is_video ? 'rgba(56,142,60,0.95)' : 'rgba(123,31,162,0.95)' }}
                                      />
                                      {src.is_video ? (
                                        <SecureVideo
                                          key={`reel-clip-${src.id}`}
                                          src={getAdminSecureVideoStreamUrl(src.id)}
                                          style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                                          controls
                                        />
                                      ) : (
                                        <SecureImage
                                          key={`reel-clip-${src.id}`}
                                          src={getAdminSecurePreviewUrl(src.id)}
                                          alt={`Source clip ${active + 1}`}
                                          style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                                        />
                                      )}
                                    </Box>
                                    {clips.length > 1 && (
                                      <IconButton
                                        onClick={() => setReelClipIndex((i) => (i + 1) % clips.length)}
                                        sx={{ position: 'absolute', right: 4, zIndex: 6, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' }, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                                      >
                                        <ChevronRight />
                                      </IconButton>
                                    )}
                                  </Box>
                                  {clips.length > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1.5 }}>
                                      {clips.map((s, di) => (
                                        <Box
                                          key={`reel-dot-${s.id}`}
                                          onClick={() => setReelClipIndex(di)}
                                          sx={{ width: di === active ? 22 : 8, height: 8, borderRadius: 4, cursor: 'pointer', transition: 'all .2s', bgcolor: di === active ? '#0288d1' : '#cbd5e1' }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </>
                              );
                            })()}
                          </CardContent>
                        </Card>
                      </Grid>

                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
                        <Typography color="text.secondary">No source clips recorded for this reel.</Typography>
                      </Box>
                    </Grid>
                  )
                ) : (
                  <>
                {/* Uploaded Image */}
                <Grid item xs={12} md={4}>
                  <Card sx={adminMediaPreviewCardSx}>
                    <CardHeader
                      title="Uploaded Image"
                      avatar={<AutoAwesome sx={{ color: '#fff' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold',
                        sx: adminMediaPreviewHeaderTitleSx
                      }}
                      sx={{
                        ...adminMediaPreviewCardHeaderBarSx,
                        background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
                        color: '#fff'
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {selectedGeneration.original_image_url ? (
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={`${API_URLS.BACKEND_BASE}${selectedGeneration.original_image_url}`}
                            alt="Uploaded"
                            style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, border: '1px solid #eee' }}
                          />
                          {/* Zoom Icon */}
                          <IconButton
                            onClick={() => handleZoomImage(
                              `${API_URLS.BACKEND_BASE}${selectedGeneration.original_image_url}`,
                              'Uploaded Image'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': {
                                bgcolor: '#f3f4f6',
                              },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10,
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography color="text.secondary">No uploaded image available.</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Customer reference photo (shown beside product / model reference when addon was used) */}
                {selectedGeneration.customer_face_image_url && (
                  <Grid item xs={12} md={4}>
                    <Card sx={adminMediaPreviewCardSx}>
                      <CardHeader
                        title="Customer photo"
                        avatar={<PersonAdd sx={{ color: '#fff' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold',
                          sx: adminMediaPreviewHeaderTitleSx
                        }}
                        sx={{
                          ...adminMediaPreviewCardHeaderBarSx,
                          background: 'linear-gradient(135deg, #00838f 0%, #004d40 100%)',
                          color: '#fff'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={`${API_URLS.BACKEND_BASE}${selectedGeneration.customer_face_image_url}`}
                            alt="Customer reference"
                            style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, border: '1px solid #eee' }}
                          />
                          <IconButton
                            onClick={() => handleZoomImage(
                              `${API_URLS.BACKEND_BASE}${selectedGeneration.customer_face_image_url}`,
                              'Customer photo'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': { bgcolor: '#f3f4f6' },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                        {/* Open side-by-side with final output (parity with AI model reference card) */}
                        <Tooltip
                          title={
                            selectedGeneration.watermarked_image_url
                              ? 'Open side-by-side with generated output'
                              : 'No generated output available to compare'
                          }
                        >
                          <span style={{ width: '100%' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              startIcon={<CompareArrows />}
                              disabled={!selectedGeneration.watermarked_image_url}
                              onClick={() => {
                                setAiModelGeneratedCompareOpen(false);
                                setCustomerPhotoGeneratedCompareOpen(true);
                              }}
                              sx={{ mt: 1.5 }}
                            >
                              Compare with generated
                            </Button>
                          </span>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* AI model catalog face used as reference (next to product upload in admin details) */}
                {selectedGeneration.model_face_image_id && (
                  <Grid item xs={12} md={4}>
                    <Card sx={adminMediaPreviewCardSx}>
                      <CardHeader
                        title="AI model reference"
                        avatar={<AutoAwesome sx={{ color: '#fff' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold',
                          sx: adminMediaPreviewHeaderTitleSx
                        }}
                        sx={{
                          ...adminMediaPreviewCardHeaderBarSx,
                          background: 'linear-gradient(135deg, #6a1b9a 0%, #311b92 100%)',
                          color: '#fff'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={getAdminModelFaceCatalogUrl(selectedGeneration.model_face_image_id)}
                            alt="AI model face"
                            style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, border: '1px solid #eee' }}
                          />
                          <IconButton
                            onClick={() => handleZoomImage(
                              getAdminModelFaceCatalogUrl(selectedGeneration.model_face_image_id),
                              'AI model reference'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': { bgcolor: '#f3f4f6' },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                        {/* Catalog display name under the preview (keeps header bar single-line like other tiles) */}
                        {selectedGeneration.model_face_catalog_name ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            title={selectedGeneration.model_face_catalog_name}
                            sx={{
                              width: '100%',
                              textAlign: 'center',
                              mt: 1.5,
                              px: 1,
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {selectedGeneration.model_face_catalog_name}
                          </Typography>
                        ) : null}
                        {/* Open side-by-side view against final output (image or video preview) */}
                        <Tooltip
                          title={
                            selectedGeneration.watermarked_image_url
                              ? 'Open side-by-side with generated output'
                              : 'No generated output available to compare'
                          }
                        >
                          <span style={{ width: '100%' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              startIcon={<CompareArrows />}
                              disabled={!selectedGeneration.watermarked_image_url}
                              onClick={() => {
                                setCustomerPhotoGeneratedCompareOpen(false);
                                setAiModelGeneratedCompareOpen(true);
                              }}
                              sx={{ mt: 1.5 }}
                            >
                              Compare with generated
                            </Button>
                          </span>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Enhanced Image for Video Generations */}
                {(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) && selectedGeneration.enhanced_image_path && (
                  <Grid item xs={12} md={4}>
                    <Card sx={adminMediaPreviewCardSx}>
                      <CardHeader
                        title="Enhanced Image"
                        avatar={<AutoFixHigh sx={{ color: '#fff' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold',
                          sx: adminMediaPreviewHeaderTitleSx
                        }}
                        sx={{
                          ...adminMediaPreviewCardHeaderBarSx,
                          background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                          color: '#fff'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={getSecurePreviewUrl(selectedGeneration.id, "enhanced")}
                            alt="Enhanced Image"
                            style={{
                              maxWidth: '100%',
                              maxHeight: 250,
                              borderRadius: 8,
                              border: '2px solid #f59e0b',
                              backgroundColor: '#fef3c7'
                            }}
                            onLoad={(e) => {
                              console.log('Enhanced image preview loaded successfully for generation:', selectedGeneration.id);
                            }}
                            onError={(e) => {
                              console.error('Enhanced image preview failed to load for generation:', selectedGeneration.id, e);
                            }}
                          />
                          {/* Zoom Icon */}
                          <IconButton
                            onClick={() => handleZoomImage(
                              getSecurePreviewUrl(selectedGeneration.id, "enhanced"),
                              'Enhanced Image'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': {
                                bgcolor: '#f3f4f6',
                              },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10,
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                  </>
                )}

                {/* Generated Content (the reel itself, when this is a reel) */}
                <Grid item xs={12} md={selectedGeneration.is_reel ? 6 : 4}>
                  <Card sx={adminMediaPreviewCardSx}>
                    <CardHeader
                      title={selectedGeneration.is_reel ? 'Reel' : ((selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) ? 'Generated Video' : 'Generated Image')}
                      avatar={<AutoAwesome sx={{ color: '#fff' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold',
                        sx: adminMediaPreviewHeaderTitleSx
                      }}
                      sx={{
                        ...adminMediaPreviewCardHeaderBarSx,
                        background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)',
                        color: '#fff'
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {selectedGeneration.watermarked_image_url ? (
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          {(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) ? (
                            // Video preview
                            <SecureVideo
                              src={getAdminGeneratedMediaUrl(selectedGeneration)}
                              style={{
                                maxWidth: '100%',
                                maxHeight: 250,
                                borderRadius: 8,
                                border: '1px solid #eee'
                              }}
                              controls
                            />
                          ) : (
                            // Image preview
                            <SecureImage
                              src={getSecureAdminUrl(selectedGeneration.watermarked_image_url)}
                              alt="Generated"
                              style={{
                                maxWidth: '100%',
                                maxHeight: 250,
                                borderRadius: 8,
                                border: '1px solid #eee'
                              }}
                            />
                          )}
                          {/* Zoom Icon - Only show for images, not videos */}
                          {!(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) && (
                            <IconButton
                              onClick={() => handleZoomImage(
                                getSecureAdminUrl(selectedGeneration.watermarked_image_url),
                                'Generated Image'
                              )}
                              sx={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                bgcolor: 'white',
                                color: 'black',
                                '&:hover': {
                                  bgcolor: '#f3f4f6',
                                },
                                width: 40,
                                height: 40,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 10,
                              }}
                            >
                              <ZoomIn sx={{ fontSize: 22 }} />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">
                          No generated {(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) ? 'video' : 'image'} available.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Prompt Details */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: 1 }}>
                    <CardHeader
                      title="Prompt Details"
                      avatar={<Description sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '8px 8px 0 0'
                      }}
                    />
                    <CardContent>
                      {selectedGeneration.prompt_registry ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {console.log('Selected Generation Data:', selectedGeneration)}

                          {/* Copy Notification */}
                          {copyNotification && (
                            <Alert
                              severity="success"
                              sx={{
                                mb: 2,
                                '& .MuiAlert-message': {
                                  fontWeight: 'medium'
                                }
                              }}
                            >
                              Prompt copied to clipboard!
                            </Alert>
                          )}
                          {/* Generated Prompt */}
                          {selectedGeneration.generated_prompt && (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">Generated Prompt</Typography>
                                <Tooltip title="Copy to clipboard">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(selectedGeneration.generated_prompt);
                                      setCopyNotification(true);
                                      setTimeout(() => setCopyNotification(false), 2000);
                                    }}
                                    sx={{
                                      color: 'primary.main',
                                      '&:hover': {
                                        backgroundColor: 'primary.light',
                                        color: 'white'
                                      }
                                    }}
                                  >
                                    <ContentCopy />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="body1" fontWeight="medium" sx={{
                                backgroundColor: '#e8f5e8',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid #c3e6c3',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {selectedGeneration.generated_prompt}
                              </Typography>
                            </Box>
                          )}

                          {/* Dictionary Data - Only show when no prompt variation is available */}
                          {selectedGeneration.dictionary_data && selectedGeneration.dictionary_data.dictionaries &&
                            !selectedGeneration.video_prompt_variation && (
                              <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="subtitle2" color="text.secondary">Dictionary Data</Typography>
                                  <Tooltip title="Copy to clipboard">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(JSON.stringify(selectedGeneration.dictionary_data.dictionaries, null, 2));
                                        setCopyNotification(true);
                                        setTimeout(() => setCopyNotification(false), 2000);
                                      }}
                                      sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                          backgroundColor: 'primary.light',
                                          color: 'white'
                                        }
                                      }}
                                    >
                                      <ContentCopy />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Typography variant="body1" fontWeight="medium" sx={{
                                  backgroundColor: '#fff3cd',
                                  p: 2,
                                  borderRadius: 1,
                                  border: '1px solid #ffeaa7',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {JSON.stringify(selectedGeneration.dictionary_data.dictionaries, null, 2)}
                                </Typography>
                              </Box>
                            )}

                          {/* Video Prompt Variation - Show for video generations or when available */}
                          {selectedGeneration.video_prompt_variation ? (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  {selectedGeneration.output_format_name === 'VIDEO' ? 'Video Generation Prompt' : 'Selected Video Prompt Variation'}
                                </Typography>
                                <Tooltip title="Copy to clipboard">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(selectedGeneration.video_prompt_variation);
                                      setCopyNotification(true);
                                      setTimeout(() => setCopyNotification(false), 2000);
                                    }}
                                    sx={{
                                      color: 'primary.main',
                                      '&:hover': {
                                        backgroundColor: 'primary.light',
                                        color: 'white'
                                      }
                                    }}
                                  >
                                    <ContentCopy />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              {selectedGeneration.output_format_name === 'VIDEO' && (
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  This is the specific prompt variation that was randomly selected and used for video generation from the available dictionary options.
                                </Typography>
                              )}
                              <Typography variant="body1" fontWeight="medium" sx={{
                                backgroundColor: selectedGeneration.output_format_name === 'VIDEO' ? '#e8f5e8' : '#e3f2fd',
                                p: 2,
                                borderRadius: 1,
                                border: selectedGeneration.output_format_name === 'VIDEO' ? '1px solid #c3e6c3' : '1px solid #90caf9',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {selectedGeneration.video_prompt_variation}
                              </Typography>
                            </Box>
                          ) : selectedGeneration.output_format_name === 'VIDEO' ? (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{
                                backgroundColor: '#fff3cd',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid #ffeaa7',
                                fontStyle: 'italic'
                              }}>
                                No video prompt variation available. The system will use the default prompt for video generation.
                              </Typography>
                            </Box>
                          ) : null}

                          {/* Generation Parameters */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {selectedGeneration.category_name || 'N/A'}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary">Model Type</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {selectedGeneration.model_type_name || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary">Output Format</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {selectedGeneration.output_format_name || 'N/A'}
                              </Typography>
                            </Grid>
                            {selectedGeneration.generation_type === 'batch' && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  Batch
                                </Typography>
                              </Grid>
                            )}
                            {selectedGeneration.batch_id && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Batch ID</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.batch_id}
                                </Typography>
                              </Grid>
                            )}


                            {(isBustModel(selectedGeneration.model_type_id)) && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Bust Color</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.bust_color_name || 'N/A'}
                                </Typography>
                              </Grid>
                            )}
                            {(isBustModel(selectedGeneration.model_type_id)) && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Bust Finish</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.bust_finish_name || 'N/A'}
                                </Typography>
                              </Grid>
                            )}
                            {(isHumanModel(selectedGeneration.model_type_id)) && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Ethnicity</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.ethnicity_name || 'N/A'}
                                </Typography>
                              </Grid>
                            )}
                            {(isHumanModel(selectedGeneration.model_type_id)) && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Clothing Type</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.clothing_type_name || 'N/A'}
                                </Typography>
                              </Grid>
                            )}

                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary">Prompt Version</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {selectedGeneration.prompt_registry?.version || 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                            No prompt registry data available for this generation.
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                            This generation may have been created before prompt registry was implemented or without using a prompt template.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* User Feedback (if available) */}
                {selectedGeneration.feedback_rating && (
                  <Grid item xs={12}>
                    <Card sx={{ boxShadow: 1 }}>
                      <CardHeader
                        title="User Feedback"
                        avatar={<InfoOutlined sx={{ color: 'white' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold'
                        }}
                        sx={{
                          backgroundColor: 'success.main',
                          color: 'white',
                          borderRadius: '8px 8px 0 0'
                        }}
                      />
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Rating</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {[...Array(5)].map((_, index) => (
                                <Box
                                  key={index}
                                  component="span"
                                  sx={{
                                    color: index < selectedGeneration.feedback_rating ? 'warning.main' : 'grey.300',
                                    fontSize: '1.5rem'
                                  }}
                                >
                                  ★
                                </Box>
                              ))}
                              <Typography variant="body1" fontWeight="medium" sx={{ ml: 1 }}>
                                {selectedGeneration.feedback_rating}/5
                              </Typography>
                            </Box>
                          </Box>
                          {selectedGeneration.feedback_comment && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Comment</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {selectedGeneration.feedback_comment}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Refund Information (if refunded) */}
                {selectedGeneration.is_refunded && (
                  <Grid item xs={12}>
                    <Card sx={{ boxShadow: 1 }}>
                      <CardHeader
                        title="Refund Information"
                        avatar={<Remove sx={{ color: 'white' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold'
                        }}
                        sx={{
                          backgroundColor: 'error.main',
                          color: 'white',
                          borderRadius: '8px 8px 0 0'
                        }}
                      />
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Refund Amount</Typography>
                            <Typography variant="body1" fontWeight="medium" color="error.main">
                              {selectedGeneration.refund_amount ? formatCredits(selectedGeneration.refund_amount) : '0'} tokens
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Refund Date</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDateTime(selectedGeneration.refund_date)}
                            </Typography>
                          </Box>
                          {selectedGeneration.refund_reason && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Refund Reason</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {selectedGeneration.refund_reason}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* API Logs Summary */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: 1 }}>
                    <CardHeader
                      title="API Processing Details"
                      avatar={<History sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        backgroundColor: 'info.main',
                        color: 'white',
                        borderRadius: '8px 8px 0 0'
                      }}
                    />
                    <CardContent>
                      {apiLogsLoading ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <CircularProgress size={20} />
                          <Typography>Loading API logs...</Typography>
                        </Box>
                      ) : apiLogsSummary ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {/* Summary Statistics */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="primary.main" fontWeight="bold">
                                  {formatCredits(apiLogsSummary.total_credits_used)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total tokens used
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="success.main" fontWeight="bold">
                                  {formatProcessingTime(apiLogsSummary.total_processing_time_seconds)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total Processing Time
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="info.main" fontWeight="bold">
                                  {apiLogsSummary.logs_count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  API Calls
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="warning.main" fontWeight="bold">
                                  {apiLogsSummary.logs.filter(log => log.response_payload).length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Logs with Response
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* API Request ID Display */}
                          {apiLogsSummary.wavespeed_id && (
                            <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="#0c4a6e" mb={1}>
                                API Request ID
                              </Typography>
                              <Typography variant="body2" color="#0c4a6e" sx={{
                                fontFamily: 'monospace',
                                backgroundColor: '#ffffff',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #e0f2fe',
                                wordBreak: 'break-all'
                              }}>
                                {apiLogsSummary.wavespeed_id}
                              </Typography>
                            </Box>
                          )}

                          {/* Detailed API Logs */}
                          {apiLogsSummary.logs && apiLogsSummary.logs.length > 0 && (
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                API Call Details
                              </Typography>
                              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                {apiLogsSummary.logs.map((log, index) => (
                                  <Card key={log.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Box>
                                        <Typography variant="body2" fontWeight="medium">
                                          {log.generation_type} - {log.model_name || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {log.api_endpoint}
                                        </Typography>
                                        {log.response_status_code && (
                                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            • Status: {log.response_status_code}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box textAlign="right">
                                        <Chip
                                          label={log.status}
                                          size="small"
                                          color={
                                            log.status === 'COMPLETED' ? 'success' :
                                              log.status === 'FAILED' ? 'error' :
                                                log.status === 'PROCESSING' ? 'warning' :
                                                  'default'
                                          }
                                        />
                                      </Box>
                                    </Box>
                                    <Box display="flex" gap={3} mt={1}>
                                      <Typography variant="caption" color="text.secondary">
                                        Tokens: {log.credits_used ? formatCredits(log.credits_used) : '0'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Processing: {formatProcessingTime(log.total_processing_time_seconds)}
                                      </Typography>
                                      {log.retry_count > 0 && (
                                        <Typography variant="caption" color="warning.main">
                                          Retries: {log.retry_count}
                                        </Typography>
                                      )}
                                      {/* Add guidance scale display - shows the guidance scale used for AI generation */}
                                      {log.guidance_scale && (
                                        <Typography variant="caption" color="text.secondary">
                                          Guidance Scale: {log.guidance_scale}
                                        </Typography>
                                      )}
                                    </Box>
                                    {log.error_message && (
                                      <Typography variant="caption" color="error.main" display="block" mt={1}>
                                        Error: {log.error_message}
                                      </Typography>
                                    )}

                                    {/* Response Payload Section */}
                                    {log.response_payload && (
                                      <Box mt={2}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            Response Payload:
                                          </Typography>
                                          <Tooltip title="Copy to clipboard">
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                navigator.clipboard.writeText(JSON.stringify(log.response_payload, null, 2));
                                                setCopyNotification(true);
                                                setTimeout(() => setCopyNotification(false), 2000);
                                              }}
                                              sx={{
                                                color: 'primary.main',
                                                '&:hover': {
                                                  backgroundColor: 'primary.light',
                                                  color: 'white'
                                                }
                                              }}
                                            >
                                              <ContentCopy sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                        <Box
                                          sx={{
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: 1,
                                            p: 1.5,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.4
                                          }}
                                        >
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {JSON.stringify(log.response_payload, null, 2)}
                                          </pre>
                                        </Box>
                                      </Box>
                                    )}
                                  </Card>
                                ))}
                              </Box>

                              {/* No Response Payloads Message */}
                              {apiLogsSummary.logs.every(log => !log.response_payload) && (
                                <Box mt={2} textAlign="center">
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Note: No response payloads are available for these API calls.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography color="text.secondary" textAlign="center">
                          No API logs available for this generation.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => setAiGenerationViewDialog(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generation View Dialog - Combined Media Preview and Metadata */}
      <Dialog
        open={aiGenerationViewDialog}
        onClose={() => setAiGenerationViewDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Visibility />
            <Typography variant="h5" component="span">
              Generation Details - #{selectedGeneration?.id}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGeneration && (
            <Box sx={{ p: 3 }}>
              {/* Header Card with Main Info */}
              <Card
                sx={{
                  mb: 3,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: 1
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        #{selectedGeneration.id}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                          Generation #{selectedGeneration.id}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {selectedGeneration.model_type_name} • {selectedGeneration.output_format_name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box textAlign="right">
                      <Chip
                        label={selectedGeneration.status_name}
                        color={
                          selectedGeneration.status_code === 'COMPLETED' ? 'success' :
                            selectedGeneration.status_code === 'FAILED' ? 'error' :
                              selectedGeneration.status_code === 'PROCESSING' ? 'warning' :
                                'default'
                        }
                        size="large"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          px: 2,
                          py: 1
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Media Preview Section */}
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }}>
                Media Preview
              </Typography>
              <Grid container spacing={3}>
                {/* Reels show their selected source clips (image or video) as inputs,
                    instead of the first clip's uploaded/enhanced image. */}
                {selectedGeneration.is_reel ? (
                  (selectedGeneration.reel_sources && selectedGeneration.reel_sources.length) ? (
                    <>
                      {/* Source clips shown as a single-card carousel: one clip at a time
                          with prev/next arrows and dot navigation. */}
                      <Grid item xs={12} md={6}>
                        <Card sx={adminMediaPreviewCardSx}>
                          <CardHeader
                            title={`Source Clips (${(reelClipIndex % selectedGeneration.reel_sources.length) + 1} / ${selectedGeneration.reel_sources.length})`}
                            avatar={<Movie sx={{ color: '#fff' }} />}
                            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', sx: adminMediaPreviewHeaderTitleSx }}
                            sx={{ ...adminMediaPreviewCardHeaderBarSx, background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)', color: '#fff' }}
                          />
                          <CardContent sx={{ flexGrow: 1 }}>
                            {(() => {
                              const clips = selectedGeneration.reel_sources;
                              const active = ((reelClipIndex % clips.length) + clips.length) % clips.length;
                              const src = clips[active];
                              return (
                                <>
                                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {clips.length > 1 && (
                                      <IconButton
                                        onClick={() => setReelClipIndex((i) => (((i - 1) % clips.length) + clips.length) % clips.length)}
                                        sx={{ position: 'absolute', left: 4, zIndex: 6, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' }, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                                      >
                                        <ChevronLeft />
                                      </IconButton>
                                    )}
                                    <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                      <Chip
                                        size="small"
                                        label={`${active + 1}  ·  ${src.is_video ? 'VIDEO' : 'IMAGE'}`}
                                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 6, height: 22, fontSize: 10, fontWeight: 700, color: '#fff', bgcolor: src.is_video ? 'rgba(56,142,60,0.95)' : 'rgba(123,31,162,0.95)' }}
                                      />
                                      {src.is_video ? (
                                        <SecureVideo
                                          key={`reel-clip-${src.id}`}
                                          src={getAdminSecureVideoStreamUrl(src.id)}
                                          style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                                          controls
                                        />
                                      ) : (
                                        <SecureImage
                                          key={`reel-clip-${src.id}`}
                                          src={getAdminSecurePreviewUrl(src.id)}
                                          alt={`Source clip ${active + 1}`}
                                          style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                                        />
                                      )}
                                    </Box>
                                    {clips.length > 1 && (
                                      <IconButton
                                        onClick={() => setReelClipIndex((i) => (i + 1) % clips.length)}
                                        sx={{ position: 'absolute', right: 4, zIndex: 6, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' }, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                                      >
                                        <ChevronRight />
                                      </IconButton>
                                    )}
                                  </Box>
                                  {clips.length > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1.5 }}>
                                      {clips.map((s, di) => (
                                        <Box
                                          key={`reel-dot-${s.id}`}
                                          onClick={() => setReelClipIndex(di)}
                                          sx={{ width: di === active ? 22 : 8, height: 8, borderRadius: 4, cursor: 'pointer', transition: 'all .2s', bgcolor: di === active ? '#0288d1' : '#cbd5e1' }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </>
                              );
                            })()}
                          </CardContent>
                        </Card>
                      </Grid>

                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
                        <Typography color="text.secondary">No source clips recorded for this reel.</Typography>
                      </Box>
                    </Grid>
                  )
                ) : (
                  <>
                {/* Uploaded Image */}
                <Grid item xs={12} md={4}>
                  <Card sx={adminMediaPreviewCardSx}>
                    <CardHeader
                      title="Uploaded Image"
                      avatar={<AutoAwesome sx={{ color: '#fff' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold',
                        sx: adminMediaPreviewHeaderTitleSx
                      }}
                      sx={{
                        ...adminMediaPreviewCardHeaderBarSx,
                        background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
                        color: '#fff'
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {selectedGeneration.original_image_url ? (
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={`${API_URLS.BACKEND_BASE}${selectedGeneration.original_image_url}`}
                            alt="Uploaded"
                            style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                          />
                          {/* Zoom Icon */}
                          <IconButton
                            onClick={() => handleZoomImage(
                              `${API_URLS.BACKEND_BASE}${selectedGeneration.original_image_url}`,
                              'Uploaded Image'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': {
                                bgcolor: '#f3f4f6',
                              },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10,
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography color="text.secondary">No uploaded image available.</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Customer reference photo (shown beside product / model reference when addon was used) */}
                {selectedGeneration.customer_face_image_url && (
                  <Grid item xs={12} md={4}>
                    <Card sx={adminMediaPreviewCardSx}>
                      <CardHeader
                        title="Customer photo"
                        avatar={<PersonAdd sx={{ color: '#fff' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold',
                          sx: adminMediaPreviewHeaderTitleSx
                        }}
                        sx={{
                          ...adminMediaPreviewCardHeaderBarSx,
                          background: 'linear-gradient(135deg, #00838f 0%, #004d40 100%)',
                          color: '#fff'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={`${API_URLS.BACKEND_BASE}${selectedGeneration.customer_face_image_url}`}
                            alt="Customer reference"
                            style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                          />
                          <IconButton
                            onClick={() => handleZoomImage(
                              `${API_URLS.BACKEND_BASE}${selectedGeneration.customer_face_image_url}`,
                              'Customer photo'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': { bgcolor: '#f3f4f6' },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                        {/* Open side-by-side with final output (parity with AI model reference card) */}
                        <Tooltip
                          title={
                            selectedGeneration.watermarked_image_url
                              ? 'Open side-by-side with generated output'
                              : 'No generated output available to compare'
                          }
                        >
                          <span style={{ width: '100%' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              startIcon={<CompareArrows />}
                              disabled={!selectedGeneration.watermarked_image_url}
                              onClick={() => {
                                setAiModelGeneratedCompareOpen(false);
                                setCustomerPhotoGeneratedCompareOpen(true);
                              }}
                              sx={{ mt: 1.5 }}
                            >
                              Compare with generated
                            </Button>
                          </span>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* AI model catalog face used as reference (next to product upload in admin details) */}
                {selectedGeneration.model_face_image_id && (
                  <Grid item xs={12} md={4}>
                    <Card sx={adminMediaPreviewCardSx}>
                      <CardHeader
                        title="AI model reference"
                        avatar={<AutoAwesome sx={{ color: '#fff' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold',
                          sx: adminMediaPreviewHeaderTitleSx
                        }}
                        sx={{
                          ...adminMediaPreviewCardHeaderBarSx,
                          background: 'linear-gradient(135deg, #6a1b9a 0%, #311b92 100%)',
                          color: '#fff'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={getAdminModelFaceCatalogUrl(selectedGeneration.model_face_image_id)}
                            alt="AI model face"
                            style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #eee' }}
                          />
                          <IconButton
                            onClick={() => handleZoomImage(
                              getAdminModelFaceCatalogUrl(selectedGeneration.model_face_image_id),
                              'AI model reference'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': { bgcolor: '#f3f4f6' },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                        {/* Catalog display name under the preview (keeps header bar single-line like other tiles) */}
                        {selectedGeneration.model_face_catalog_name ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            title={selectedGeneration.model_face_catalog_name}
                            sx={{
                              width: '100%',
                              textAlign: 'center',
                              mt: 1.5,
                              px: 1,
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {selectedGeneration.model_face_catalog_name}
                          </Typography>
                        ) : null}
                        {/* Open side-by-side view against final output (image or video preview) */}
                        <Tooltip
                          title={
                            selectedGeneration.watermarked_image_url
                              ? 'Open side-by-side with generated output'
                              : 'No generated output available to compare'
                          }
                        >
                          <span style={{ width: '100%' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              startIcon={<CompareArrows />}
                              disabled={!selectedGeneration.watermarked_image_url}
                              onClick={() => {
                                setCustomerPhotoGeneratedCompareOpen(false);
                                setAiModelGeneratedCompareOpen(true);
                              }}
                              sx={{ mt: 1.5 }}
                            >
                              Compare with generated
                            </Button>
                          </span>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Enhanced Image for Video Generations */}
                {(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) && selectedGeneration.enhanced_image_path && (
                  <Grid item xs={12} md={4}>
                    <Card sx={adminMediaPreviewCardSx}>
                      <CardHeader
                        title="Enhanced Image"
                        avatar={<AutoFixHigh sx={{ color: '#fff' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold',
                          sx: adminMediaPreviewHeaderTitleSx
                        }}
                        sx={{
                          ...adminMediaPreviewCardHeaderBarSx,
                          background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                          color: '#fff'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          <SecureImage
                            src={getSecurePreviewUrl(selectedGeneration.id, "enhanced")}
                            alt="Enhanced Image"
                            style={{
                              maxWidth: '100%',
                              maxHeight: 300,
                              borderRadius: 8,
                              border: '2px solid #f59e0b',
                              backgroundColor: '#fef3c7'
                            }}
                            onLoad={(e) => {
                              console.log('Enhanced image preview loaded successfully for generation:', selectedGeneration.id);
                            }}
                            onError={(e) => {
                              console.error('Enhanced image preview failed to load for generation:', selectedGeneration.id, e);
                            }}
                          />
                          {/* Zoom Icon */}
                          <IconButton
                            onClick={() => handleZoomImage(
                              getSecurePreviewUrl(selectedGeneration.id, "enhanced"),
                              'Enhanced Image'
                            )}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              bgcolor: 'white',
                              color: 'black',
                              '&:hover': {
                                bgcolor: '#f3f4f6',
                              },
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 10,
                            }}
                          >
                            <ZoomIn sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                  </>
                )}

                {/* Generated Content (the reel itself, when this is a reel) */}
                <Grid item xs={12} md={selectedGeneration.is_reel ? 6 : 4}>
                  <Card sx={adminMediaPreviewCardSx}>
                    <CardHeader
                      title={selectedGeneration.is_reel ? 'Reel' : ((selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) ? 'Generated Video' : 'Generated Image')}
                      avatar={<AutoAwesome sx={{ color: '#fff' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold',
                        sx: adminMediaPreviewHeaderTitleSx
                      }}
                      sx={{
                        ...adminMediaPreviewCardHeaderBarSx,
                        background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)',
                        color: '#fff'
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {selectedGeneration.watermarked_image_url ? (
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                          {(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) ? (
                            // Video preview
                            <SecureVideo
                              src={getAdminGeneratedMediaUrl(selectedGeneration)}
                              style={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                borderRadius: 8,
                                border: '1px solid #eee'
                              }}
                              controls
                            />
                          ) : (
                            // Image preview
                            <SecureImage
                              src={getSecureAdminUrl(selectedGeneration.watermarked_image_url)}
                              alt="Generated"
                              style={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                borderRadius: 8,
                                border: '1px solid #eee'
                              }}
                            />
                          )}
                          {/* Zoom Icon - Only show for images, not videos */}
                          {!(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) && (
                            <IconButton
                              onClick={() => handleZoomImage(
                                getSecureAdminUrl(selectedGeneration.watermarked_image_url),
                                'Generated Image'
                              )}
                              sx={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                bgcolor: 'white',
                                color: 'black',
                                '&:hover': {
                                  bgcolor: '#f3f4f6',
                                },
                                width: 40,
                                height: 40,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 10,
                              }}
                            >
                              <ZoomIn sx={{ fontSize: 22 }} />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">
                          No generated {(selectedGeneration.output_format_name === 'VIDEO' || selectedGeneration.generated_video_path) ? 'video' : 'image'} available.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Metadata Details Section */}
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: 'primary.main', mt: 4 }}>
                Metadata Details
              </Typography>

              <Grid container spacing={3}>
                {/* Prompt Details */}
                {canViewAIGenerationPrompts && (
                  <Grid item xs={12}>
                    <Card sx={{ boxShadow: 1 }}>
                      <CardHeader
                        title="Prompt Details"
                        avatar={<Description sx={{ color: 'white' }} />}
                        titleTypographyProps={{
                          variant: 'h6',
                          fontWeight: 'bold'
                        }}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          borderRadius: '8px 8px 0 0'
                        }}
                      />
                      <CardContent>
                        {selectedGeneration.prompt_registry ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Generated Prompt */}
                            {selectedGeneration.generated_prompt && (
                              <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="subtitle2" color="text.secondary">Generated Prompt</Typography>
                                  <Tooltip title="Copy to clipboard">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(selectedGeneration.generated_prompt);
                                        setCopyNotification(true);
                                        setTimeout(() => setCopyNotification(false), 2000);
                                      }}
                                      sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                          backgroundColor: 'primary.light',
                                          color: 'white'
                                        }
                                      }}
                                    >
                                      <ContentCopy />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Typography variant="body1" fontWeight="medium" sx={{
                                  backgroundColor: '#e8f5e8',
                                  p: 2,
                                  borderRadius: 1,
                                  border: '1px solid #c3e6c3',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {selectedGeneration.generated_prompt}
                                </Typography>
                              </Box>
                            )}

                            {/* Video Prompt Variation - Show for video generations */}
                            {selectedGeneration.video_prompt_variation && (
                              <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="subtitle2" color="text.secondary">
                                    Video Generation Prompt
                                  </Typography>
                                  <Tooltip title="Copy to clipboard">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(selectedGeneration.video_prompt_variation);
                                        setCopyNotification(true);
                                        setTimeout(() => setCopyNotification(false), 2000);
                                      }}
                                      sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                          backgroundColor: 'primary.light',
                                          color: 'white'
                                        }
                                      }}
                                    >
                                      <ContentCopy />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  This is the specific prompt variation that was randomly selected and used for video generation from the available dictionary options.
                                </Typography>
                                <Typography variant="body1" fontWeight="medium" sx={{
                                  backgroundColor: '#e3f2fd',
                                  p: 2,
                                  borderRadius: 1,
                                  border: '1px solid #90caf9',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {selectedGeneration.video_prompt_variation}
                                </Typography>
                              </Box>
                            )}

                            {/* Generation Parameters */}
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.category_name || 'N/A'}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Model Type</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.model_type_name || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Output Format</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedGeneration.output_format_name || 'N/A'}
                                </Typography>
                              </Grid>
                              {selectedGeneration.gender_name && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {selectedGeneration.gender_name}
                                  </Typography>
                                </Grid>
                              )}
                              {selectedGeneration.ethnicity_name && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" color="text.secondary">Ethnicity</Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {selectedGeneration.ethnicity_name}
                                  </Typography>
                                </Grid>
                              )}
                              {selectedGeneration.clothing_type_name && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" color="text.secondary">Clothing Type</Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {selectedGeneration.clothing_type_name}
                                  </Typography>
                                </Grid>
                              )}
                              {selectedGeneration.bust_color_name && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" color="text.secondary">Bust Color</Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {selectedGeneration.bust_color_name}
                                  </Typography>
                                </Grid>
                              )}
                              {selectedGeneration.bust_finish_name && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" color="text.secondary">Bust Finish</Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {selectedGeneration.bust_finish_name}
                                  </Typography>
                                </Grid>
                              )}
                              {selectedGeneration.watermark_type && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" color="text.secondary">Watermark Type</Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {selectedGeneration.watermark_type === 'BOTH' ? 'Both Watermarks' :
                                      selectedGeneration.watermark_type === 'GLAMMIFY' ? 'Glammify Logo' :
                                        selectedGeneration.watermark_type === 'BRAND' ? 'Brand Watermark' :
                                          selectedGeneration.watermark_type === 'NONE' ? 'No Watermark' : selectedGeneration.watermark_type}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        ) : (
                          <Typography color="text.secondary" textAlign="center">
                            No prompt registry data available for this generation.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* API Logs Summary */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: 1 }}>
                    <CardHeader
                      title="API Processing Details"
                      avatar={<History sx={{ color: 'white' }} />}
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 'bold'
                      }}
                      sx={{
                        backgroundColor: 'info.main',
                        color: 'white',
                        borderRadius: '8px 8px 0 0'
                      }}
                    />
                    <CardContent>
                      {apiLogsLoading ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <CircularProgress size={20} />
                          <Typography>Loading API logs...</Typography>
                        </Box>
                      ) : apiLogsSummary ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {/* Summary Statistics */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="primary.main" fontWeight="bold">
                                  {formatCredits(apiLogsSummary.total_credits_used)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total tokens used
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="success.main" fontWeight="bold">
                                  {formatProcessingTime(apiLogsSummary.total_processing_time_seconds)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total Processing Time
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="info.main" fontWeight="bold">
                                  {apiLogsSummary.logs_count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  API Calls
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h4" color="warning.main" fontWeight="bold">
                                  {apiLogsSummary.logs.filter(log => log.response_payload).length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Logs with Response
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* API Request ID Display */}
                          {apiLogsSummary.wavespeed_id && (
                            <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="#0c4a6e" mb={1}>
                                API Request ID
                              </Typography>
                              <Typography variant="body2" color="#0c4a6e" sx={{
                                fontFamily: 'monospace',
                                backgroundColor: '#ffffff',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #e0f2fe',
                                wordBreak: 'break-all'
                              }}>
                                {apiLogsSummary.wavespeed_id}
                              </Typography>
                            </Box>
                          )}

                          {/* Detailed API Logs */}
                          {apiLogsSummary.logs && apiLogsSummary.logs.length > 0 && (
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                API Call Details
                              </Typography>
                              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                {apiLogsSummary.logs.map((log, index) => (
                                  <Card key={log.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Box>
                                        <Typography variant="body2" fontWeight="medium">
                                          {log.generation_type} - {log.model_name || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {log.api_endpoint}
                                        </Typography>
                                        {log.response_status_code && (
                                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            • Status: {log.response_status_code}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box textAlign="right">
                                        <Chip
                                          label={log.status}
                                          size="small"
                                          color={
                                            log.status === 'COMPLETED' ? 'success' :
                                              log.status === 'FAILED' ? 'error' :
                                                log.status === 'PROCESSING' ? 'warning' :
                                                  'default'
                                          }
                                        />
                                      </Box>
                                    </Box>
                                    <Box display="flex" gap={3} mt={1}>
                                      <Typography variant="caption" color="text.secondary">
                                        Tokens: {log.credits_used ? formatCredits(log.credits_used) : '0'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Processing: {formatProcessingTime(log.total_processing_time_seconds)}
                                      </Typography>
                                      {log.retry_count > 0 && (
                                        <Typography variant="caption" color="warning.main">
                                          Retries: {log.retry_count}
                                        </Typography>
                                      )}
                                      {log.guidance_scale && (
                                        <Typography variant="caption" color="text.secondary">
                                          Guidance Scale: {log.guidance_scale}
                                        </Typography>
                                      )}
                                    </Box>
                                    {log.error_message && (
                                      <Typography variant="caption" color="error.main" display="block" mt={1}>
                                        Error: {log.error_message}
                                      </Typography>
                                    )}

                                    {/* Response Payload Section */}
                                    {log.response_payload && (
                                      <Box mt={2}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            Response Payload:
                                          </Typography>
                                          <Tooltip title="Copy to clipboard">
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                navigator.clipboard.writeText(JSON.stringify(log.response_payload, null, 2));
                                                setCopyNotification(true);
                                                setTimeout(() => setCopyNotification(false), 2000);
                                              }}
                                              sx={{
                                                color: 'primary.main',
                                                '&:hover': {
                                                  backgroundColor: 'primary.light',
                                                  color: 'white'
                                                }
                                              }}
                                            >
                                              <ContentCopy sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                        <Box
                                          sx={{
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: 1,
                                            p: 1.5,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.4
                                          }}
                                        >
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {JSON.stringify(log.response_payload, null, 2)}
                                          </pre>
                                        </Box>
                                      </Box>
                                    )}
                                  </Card>
                                ))}
                              </Box>

                              {/* No Response Payloads Message */}
                              {apiLogsSummary.logs.every(log => !log.response_payload) && (
                                <Box mt={2} textAlign="center">
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Note: No response payloads are available for these API calls.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography color="text.secondary" textAlign="center">
                          No API logs available for this generation.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => setAiGenerationViewDialog(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        open={refundDialog}
        onClose={() => setRefundDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 3,
            pt: 3,
            px: 4,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <AccountBalanceWallet sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="span" fontWeight="bold">
                Process Full Refund
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Issue a full refund for this generation ({selectedGeneration?.credits_cost ? formatCredits(selectedGeneration.credits_cost) : '0'} tokens)
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGeneration && (
            <Box sx={{ p: 4 }}>
              {/* Generation Info Card */}
              <Card
                sx={{
                  mb: 4,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  boxShadow: 'none'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      #{selectedGeneration.id}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Generation #{selectedGeneration.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedGeneration.model_type_name} • {selectedGeneration.output_format_name}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${formatCredits(selectedGeneration.credits_cost)} tokens`}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        fontSize: '0.875rem'
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Merchant</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedGeneration.merchant_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedGeneration.merchant_email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip
                        label={selectedGeneration.status_name}
                        color={
                          selectedGeneration.status_code === 'COMPLETED' ? 'success' :
                            selectedGeneration.status_code === 'FAILED' ? 'error' :
                              selectedGeneration.status_code === 'PROCESSING' ? 'warning' :
                                'default'
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Refund Form */}
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  Refund Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Refund amount (tokens)"
                      type="number"
                      value={selectedGeneration.credits_cost}
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#f8fafc',
                          '& fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f8fafc',
                            '& fieldset': {
                              borderColor: '#d1d5db',
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          color: '#374151',
                          '&.Mui-disabled': {
                            color: '#374151',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#1f2937',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, color: '#6b7280' }}>
                            <AccountBalanceWallet sx={{ fontSize: 20 }} />
                          </Box>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Full amount will be refunded: {selectedGeneration.credits_cost ? formatCredits(selectedGeneration.credits_cost) : '0'} tokens
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                      <Box
                        sx={{
                          flex: 1,
                          p: 2,
                          backgroundColor: '#fef3c7',
                          borderRadius: 2,
                          border: '1px solid #f59e0b',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" color="#92400e" fontWeight="600">
                          Original Cost
                        </Typography>
                        <Typography variant="h5" color="#92400e" fontWeight="bold">
                          {selectedGeneration.credits_cost ? formatCredits(selectedGeneration.credits_cost) : '0'} tokens
                        </Typography>
                      </Box>
                      <Box sx={{ color: '#6b7280' }}>
                        <AccountBalanceWallet sx={{ fontSize: 24 }} />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          p: 2,
                          backgroundColor: '#fee2e2',
                          borderRadius: 2,
                          border: '1px solid #ef4444',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" color="#b91c1c" fontWeight="600">
                          Refund Amount
                        </Typography>
                        <Typography variant="h5" color="#b91c1c" fontWeight="bold">
                          {selectedGeneration.credits_cost ? formatCredits(selectedGeneration.credits_cost) : '0'} tokens
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Refund Reason"
                      multiline
                      rows={4}
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Please provide a detailed reason for this refund..."
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9ca3af',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ef4444',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, color: '#6b7280', mt: 1 }}>
                            <InfoOutlined sx={{ fontSize: 20 }} />
                          </Box>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      This reason will be visible to the merchant and recorded for audit purposes
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setRefundDialog(false)}
            variant="outlined"
            disabled={refundLoading}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessRefund}
            variant="contained"
            disabled={refundLoading || !refundAmount || !refundReason || !canProcessAIGenerationRefund}
            startIcon={refundLoading ? <CircularProgress size={20} color="inherit" /> : <AccountBalanceWallet />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.4)',
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af',
                boxShadow: 'none'
              }
            }}
          >
            {refundLoading ? 'Processing Refund...' : 'Process Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced compare: split / slider, zoom, swap, fullscreen (AI model reference vs output) */}
      <GenerationCompareDialog
        open={aiModelGeneratedCompareOpen}
        onClose={() => setAiModelGeneratedCompareOpen(false)}
        generationId={selectedGeneration?.id}
        titleSecondary={
          selectedGeneration?.model_face_catalog_name
            ? `Model: ${selectedGeneration.model_face_catalog_name}`
            : null
        }
        referenceLabel="AI model reference"
        referenceSrc={
          selectedGeneration?.model_face_image_id
            ? getAdminModelFaceCatalogUrl(selectedGeneration.model_face_image_id)
            : null
        }
        generatedSrc={
          selectedGeneration ? getAdminGeneratedMediaUrl(selectedGeneration) : null
        }
        isVideoOutput={
          selectedGeneration?.output_format_name === 'VIDEO' ||
          Boolean(selectedGeneration?.generated_video_path)
        }
      />

      {/* Same advanced compare for customer reference vs output */}
      <GenerationCompareDialog
        open={customerPhotoGeneratedCompareOpen}
        onClose={() => setCustomerPhotoGeneratedCompareOpen(false)}
        generationId={selectedGeneration?.id}
        titleSecondary="Customer reference photo"
        referenceLabel="Customer photo"
        referenceSrc={
          selectedGeneration?.customer_face_image_url
            ? `${API_URLS.BACKEND_BASE}${selectedGeneration.customer_face_image_url}`
            : null
        }
        generatedSrc={
          selectedGeneration ? getAdminGeneratedMediaUrl(selectedGeneration) : null
        }
        isVideoOutput={
          selectedGeneration?.output_format_name === 'VIDEO' ||
          Boolean(selectedGeneration?.generated_video_path)
        }
      />

      {/* Zoom Dialog */}
      <Dialog
        open={zoomDialog}
        onClose={() => setZoomDialog(false)}
        maxWidth={false}
        fullWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
            padding: 20,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            maxHeight: 'none',
            width: 'auto',
            height: 'auto',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {zoomedImage && (
            <>
              <SecureImage
                src={zoomedImage.src}
                alt={`Zoomed ${zoomedImage.type && typeof zoomedImage.type.toLowerCase === 'function' ? zoomedImage.type.toLowerCase() : (typeof zoomedImage.type === 'string' ? zoomedImage.type : 'image')}`}
                style={{
                  maxWidth: '85vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  backgroundColor: '#000'
                }}
                onLoad={() => {}}
                onError={() => {}}
              />
              {/* Close Button - Positioned outside the image */}
              <IconButton
                onClick={() => setZoomDialog(false)}
                sx={{
                  position: 'absolute',
                  top: -25,
                  right: -25,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                  },
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  zIndex: 10,
                }}
              >
                <Cancel sx={{ fontSize: 25 }} />
              </IconButton>
            </>
          )}
        </Box>
      </Dialog>

      {/* Topic Notification Dialog */}
      <Dialog
        open={topicNotificationDialog}
        onClose={() => setTopicNotificationDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 3,
            pt: 3,
            px: 4,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Notifications sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="span" fontWeight="bold">
                Send Broadcast Notification
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Send notification to all users subscribed to configured topic
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 4 }}>
            {/* Success/Error Messages */}
            {topicNotificationSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {topicNotificationSuccess}
              </Alert>
            )}
            {topicNotificationError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {topicNotificationError}
              </Alert>
            )}

            {/* Topic Info Card */}
            <Card
              sx={{
                mb: 4,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                boxShadow: 'none'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <InfoOutlined color="primary" />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Broadcast Configuration
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  This notification will be sent to all users subscribed to the configured topic.
                </Typography>
              </CardContent>
            </Card>

            {/* Notification Form */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notification Title"
                  value={topicNotificationForm.title}
                  onChange={(e) => handleTopicNotificationChange('title', e.target.value)}
                  placeholder="Enter notification title..."
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#6b7280', mt: 1 }}>
                        <Typography sx={{ fontSize: 16 }}>📢</Typography>
                      </Box>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notification Body"
                  multiline
                  rows={4}
                  value={topicNotificationForm.body}
                  onChange={(e) => handleTopicNotificationChange('body', e.target.value)}
                  placeholder="Enter notification message..."
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#6b7280', mt: 1 }}>
                        <Typography sx={{ fontSize: 16 }}>💬</Typography>
                      </Box>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL (Optional)"
                  value={topicNotificationForm.image_url}
                  onChange={(e) => handleTopicNotificationChange('image_url', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#6b7280', mt: 1 }}>
                        <Typography sx={{ fontSize: 16 }}>🖼️</Typography>
                      </Box>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Optional: Add an image URL to display with the notification
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Broadcast Message (Optional)"
                  value={topicNotificationForm.topic_message}
                  onChange={(e) => handleTopicNotificationChange('topic_message', e.target.value)}
                  placeholder="Enter broadcast message or label..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#6b7280', mt: 1 }}>
                        <Typography sx={{ fontSize: 16 }}>📢</Typography>
                      </Box>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Optional: Add a broadcast message that will be highlighted in the history
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setTopicNotificationDialog(false)}
            variant="outlined"
            disabled={topicNotificationLoading}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendTopicNotification}
            variant="contained"
            disabled={topicNotificationLoading || !topicNotificationForm.title || !topicNotificationForm.body}
            startIcon={topicNotificationLoading ? <CircularProgress size={20} color="inherit" /> : <Notifications />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af',
                boxShadow: 'none'
              }
            }}
          >
            {topicNotificationLoading ? 'Sending Notification...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Topic Notification View Dialog */}
      <Dialog
        open={topicNotificationViewDialog}
        onClose={() => setTopicNotificationViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 3,
            pt: 3,
            px: 4,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Visibility sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="span" fontWeight="bold">
                Topic Notification Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                View detailed information about the topic notification
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedTopicNotification && (
            <Box sx={{ p: 4 }}>
              {/* Status and Basic Info */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Status Information
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Chip
                          label={getTopicNotificationStatusLabel(selectedTopicNotification.topic_status)}
                          color={getTopicNotificationStatusColor(selectedTopicNotification.topic_status)}
                          size="medium"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Message ID:</strong> {selectedTopicNotification.topic_message_id || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Success Count:</strong> {selectedTopicNotification.topic_success_count || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Failure Count:</strong> {selectedTopicNotification.topic_failure_count || 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Sender Information
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Sender ID:</strong> {selectedTopicNotification.topic_sender_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Sender Name:</strong> {selectedTopicNotification.topic_sender_name || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Topic Name:</strong> {selectedTopicNotification.topic_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Created:</strong> {formatDateTime(selectedTopicNotification.topic_created_at)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Notification Content */}
              <Card sx={{ mb: 4, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Notification Content
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Title
                    </Typography>
                    <Typography variant="body1" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                      {selectedTopicNotification.topic_title}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Body
                    </Typography>
                    <Typography variant="body1" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, border: '1px solid #e2e8f0', whiteSpace: 'pre-wrap' }}>
                      {selectedTopicNotification.topic_body}
                    </Typography>
                  </Box>

                  {selectedTopicNotification.topic_message && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Broadcast Message
                      </Typography>
                      <Chip
                        label={selectedTopicNotification.topic_message}
                        color="primary"
                        variant="outlined"
                        size="large"
                        sx={{ fontSize: '1rem', p: 1 }}
                      />
                    </Box>
                  )}

                  {selectedTopicNotification.topic_image_url && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Image URL
                      </Typography>
                      <Typography variant="body2" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, border: '1px solid #e2e8f0', wordBreak: 'break-all' }}>
                        {selectedTopicNotification.topic_image_url}
                      </Typography>
                    </Box>
                  )}

                  {selectedTopicNotification.topic_data && Object.keys(selectedTopicNotification.topic_data).length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Additional Data
                      </Typography>
                      <Typography variant="body2" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, border: '1px solid #e2e8f0', fontFamily: 'monospace' }}>
                        {JSON.stringify(selectedTopicNotification.topic_data, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Error Information */}
              {selectedTopicNotification.topic_error_message && (
                <Card sx={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" color="error" gutterBottom>
                      Error Information
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, border: '1px solid #ef4444' }}>
                      {selectedTopicNotification.topic_error_message}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setTopicNotificationViewDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmationDialog}
        onClose={() => setDeleteConfirmationDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 3,
            pt: 3,
            px: 4,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Delete sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="span" fontWeight="bold">
                Delete Notification
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {notificationToDelete && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this topic notification?
              </Typography>
              <Card sx={{ mt: 2, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" color="error" gutterBottom>
                    "{notificationToDelete.topic_title}"
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notificationToDelete.topic_body.length > 100
                      ? `${notificationToDelete.topic_body.substring(0, 100)}...`
                      : notificationToDelete.topic_body}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Sent on: {formatDateTime(notificationToDelete.topic_created_at)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This will permanently delete the notification from the history. This action cannot be undone.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setDeleteConfirmationDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteTopicNotification}
            variant="contained"
            color="error"
            startIcon={<Delete />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.4)',
              }
            }}
          >
            Delete Notification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Referral Code Assign Dialog */}
      <Dialog
        open={referralCodeAssignDialog}
        onClose={() => setReferralCodeAssignDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <PersonAdd />
            <Typography variant="h5" component="span">
              Assign Referral Code
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedReferralCode && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Assign referral code <strong>{selectedReferralCode.code}</strong> to a merchant:
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Merchant</InputLabel>
                <Select
                  value={assignMerchantId}
                  label="Select Merchant"
                  onChange={(e) => setAssignMerchantId(e.target.value)}
                >
                  {merchants.map((merchant) => (
                    <MenuItem key={merchant.id} value={merchant.id}>
                      {merchant.name} ({merchant.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {referralCodeDialogError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {referralCodeDialogError}
                </Alert>
              )}

              {referralCodeDialogSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {referralCodeDialogSuccess}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setReferralCodeAssignDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignReferralCode}
            variant="contained"
            startIcon={<PersonAdd />}
            disabled={!assignMerchantId || referralCodeDialogLoading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
              }
            }}
          >
            {referralCodeDialogLoading ? <CircularProgress size={20} color="inherit" /> : 'Assign Code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Referral Code Release Dialog */}
      <Dialog
        open={referralCodeReleaseDialog}
        onClose={() => setReferralCodeReleaseDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            backgroundColor: 'error.main',
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <PersonRemove />
            <Typography variant="h5" component="span">
              Release Referral Code
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedReferralCode && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to release referral code <strong>{selectedReferralCode.code}</strong>?
              </Typography>

              {selectedReferralCode.assigned_merchant && (
                <Card sx={{ mt: 2, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Currently assigned to: <strong>{selectedReferralCode.assigned_merchant.name}</strong>
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This will make the referral code available for assignment to another merchant.
                </Typography>
              </Alert>

              {referralCodeDialogError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {referralCodeDialogError}
                </Alert>
              )}

              {referralCodeDialogSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {referralCodeDialogSuccess}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setReferralCodeReleaseDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReleaseReferralCode}
            variant="contained"
            color="error"
            startIcon={<PersonRemove />}
            disabled={referralCodeDialogLoading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 6px -1px rgba(239, 68, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.4)',
              }
            }}
          >
            {referralCodeDialogLoading ? <CircularProgress size={20} color="inherit" /> : 'Release Code'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantManagement; 